import React, { useState, useEffect, useRef, createContext, useContext, useMemo } from 'react';
import './App.css';
import './components/OPODashboard.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  Link,
  useLocation,
  Navigate
} from 'react-router';
import Fuse from 'fuse.js';
import FeedbackManager from './components/FeedbackManager.js';
import WishListManager from './components/WishListManager.js';
import MarketingPage from './components/MarketingPage.js';
import CommunicationsDashboard from './components/CommunicationsDashboard.js';
import SystemAlertsModal, { isSystemAlertsDismissed } from './components/SystemAlertsModal.jsx';
import SystemMonitor from './components/SystemMonitor.js';
import OPODashboard from './components/OPODashboard.js';
import SecurityDashboard from './components/SecurityDashboard.js';
import BackupsDashboard from './components/BackupsDashboard.js';
import SpreadsheetViewerPage from './components/SpreadsheetViewerPage.js';
import T2ReviewPage from './components/T2ReviewPage.js';
import AuditorPage from './components/AuditorPage.js';
import AdminCorneliusTasks from './pages/AdminCorneliusTasks';
import AdminCorneliusMemories from './pages/AdminCorneliusMemories';
import ThemeToggle from './components/common/ThemeToggle';
import DemoModeBanner from './components/DemoModeBanner';
import { API_URL, MAIN_BACKEND_URL, adminSessionHeaders } from './lib/api';
import { parseServicePackageFeatures, buildRetailCompanyAddressString, buildBuildingAddressString } from './lib/formatters';
import { formatOccupancyCount } from './lib/propertyTypes';
import UserModal from './components/modals/UserModal';
import UserJourneyTimeline from './components/UserJourneyTimeline';
import BuildingModal from './components/modals/BuildingModal';
import LoginPage from './pages/auth/LoginPage';
import PasswordReset from './pages/auth/PasswordReset';
import PackageModal from './components/modals/PackageModal';
import RetailCompanyModal from './components/modals/RetailCompanyModal';
import AdminSettingsModal from './components/modals/AdminSettingsModal';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix for default markers in react-leaflet (ESM — Vite has no require())
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});


// Create context for sidebar state
const SidebarContext = createContext();

// Sidebar Provider Component
function SidebarProvider({ children }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed }}>
      {children}
    </SidebarContext.Provider>
  );
}

// Theme Toggle Component
// User Modal Component with Building-Role Structure




// Shared Theme Context for consistent theme across all pages
const ThemeContext = React.createContext();

// Enhanced TopNav Component with functional buttons
function TopNav({ showSearch = true, searchData = [], searchType = '', onResultSelect }) {
  const { isDark, toggleTheme } = React.useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Smart search only on /users
  const showSmartSearch = showSearch && location.pathname.startsWith('/users') && searchData.length > 0;
  // Smart search state
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const searchInputRef = useRef();
  const dropdownRef = useRef();

  // Fuse.js setup
  const fuse = React.useMemo(() => new Fuse(searchData, {
    keys: [
      'firstName',
      'lastName',
      'username',
      'email',
      'telNo',
      'country',
      'userTypes',
    ],
    threshold: 0.3,
    includeMatches: true,
    minMatchCharLength: 2,
  }), [searchData]);

  useEffect(() => {
    if (!showSmartSearch) return;
    if (search.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    const results = fuse.search(search).slice(0, 8);
    setSearchResults(results);
    setShowDropdown(true);
    setHighlightIndex(0);
  }, [search, fuse, showSmartSearch]);

  // Keyboard navigation
  useEffect(() => {
    if (!showSmartSearch) return;
    function handleKeyDown(e) {
      if (!showDropdown) return;
      if (e.key === 'ArrowDown') {
        setHighlightIndex(i => Math.min(i + 1, searchResults.length - 1));
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        setHighlightIndex(i => Math.max(i - 1, 0));
        e.preventDefault();
      } else if (e.key === 'Enter') {
        if (searchResults[highlightIndex]) {
          if (onResultSelect) onResultSelect(searchResults[highlightIndex].item);
          setShowDropdown(false);
        }
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDropdown, searchResults, highlightIndex, onResultSelect, showSmartSearch]);

  // Click outside to close dropdown
  useEffect(() => {
    if (!showSmartSearch) return;
    function handleClick(e) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSmartSearch]);

  // Highlight matched text
  function highlightMatch(text, matches, key) {
    if (!matches) return text;
    const match = matches.find(m => m.key === key);
    if (!match) return text;
    let result = '';
    let lastIndex = 0;
    match.indices.forEach(([start, end], i) => {
      result += text.slice(lastIndex, start);
      result += `<b>${text.slice(start, end + 1)}</b>`;
      lastIndex = end + 1;
    });
    result += text.slice(lastIndex);
    return result;
  }

  const handleLogout = () => {
    // Clear any auth tokens/session data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userSession');
    // Navigate to login
    navigate('/');
  };

  const handleNotifications = () => {
    // Show notification panel or modal
    alert('Notifications feature - Coming soon!\n\nThis will show:\n• User management alerts\n• System notifications\n• Audit trail updates\n• Building activity reports');
  };

  const handleSettings = () => {
    // Create and dispatch a custom event to open admin settings modal
    const settingsEvent = new CustomEvent('openAdminSettings');
    window.dispatchEvent(settingsEvent);
  };

  return (
    <div className="top-nav">
      {showSmartSearch ? (
        <div className="search-container" style={{ position: 'relative', width: 440 }}>
          <input
            ref={searchInputRef}
            type="text"
            className="global-search"
            placeholder="Search users by name, email, phone, country, etc..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => search.length > 1 && setShowDropdown(true)}
            style={{ width: '100%', fontSize: '1.1rem', paddingLeft: 40 }}
          />
          <i className="fas fa-search search-icon" style={{ left: 12, top: 14 }}></i>
          {showDropdown && searchResults.length > 0 && (
            <div ref={dropdownRef} style={{
              position: 'absolute',
              top: 44,
              left: 0,
              width: '100%',
              background: 'var(--surface-color)',
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              boxShadow: 'var(--shadow-md)',
              zIndex: 10,
              maxHeight: 320,
              overflowY: 'auto',
            }}>
              {searchResults.map((res, idx) => (
                <div
                  key={res.item.id}
                  className="nav-item"
                  style={{
                    background: idx === highlightIndex ? 'var(--primary-color)' : 'transparent',
                    color: idx === highlightIndex ? 'var(--text-on-primary)' : 'var(--text-primary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                  }}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  onClick={() => {
                    if (onResultSelect) onResultSelect(res.item);
                    setShowDropdown(false);
                  }}
                >
                  <span style={{ fontSize: 20, marginRight: 8 }}>👤</span>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: highlightMatch(res.item.firstName + ' ' + res.item.lastName, res.matches, 'firstName')
                    }}
                  />
                  <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>
                    {res.item.email}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        showSearch && (
          <div className="search-container">
            <i className="fas fa-search search-icon"></i>
            <input type="text" placeholder="Search..." className="global-search" />
          </div>
        )
      )}
      <div className="top-nav-actions">
        <button 
          className="icon-button settings-button" 
          onClick={handleSettings}
          title="Admin Settings"
        >
          <i className="fas fa-cog"></i>
        </button>
        <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
        <button 
          className="icon-button logout-button" 
          onClick={handleLogout}
          title="Logout"
        >
          <i className="fas fa-sign-out-alt"></i>
        </button>
      </div>
    </div>
  );
}

// Sidebar — includes Databases (inventory) link
function Sidebar({ className = '', onMobileToggle }) {
  const location = useLocation();
  const { isCollapsed, setIsCollapsed } = useContext(SidebarContext);
  
  // Check if we're on mobile
  const isMobile = window.innerWidth <= 768;
  
  const navItems = [
    { to: '/dashboard', icon: 'fas fa-tachometer-alt', label: 'Overview Dashboard' },
    { to: '/databases', icon: 'fas fa-database', label: 'Databases' },
    { to: '/users', icon: 'fas fa-users', label: 'BETA Users' },
    { to: '/retail-users', icon: 'fas fa-building', label: 'Enterprise Users' },
    { to: '/packages', icon: 'fas fa-box', label: 'Package Management' },
    { to: '/feedback', icon: 'fas fa-comments', label: 'Feedback' },
    { to: '/widgets', icon: 'fas fa-th-large', label: 'Widgets' },
    { to: '/marketing', icon: 'fas fa-bullhorn', label: 'Marketing' },
    { to: '/communications', icon: 'fas fa-envelope', label: 'Communications' },
    { to: '/opo', icon: 'fas fa-brain', label: 'OPO' },
    { to: '/security', icon: 'fas fa-shield-alt', label: 'Security' },
    { to: '/backups', icon: 'fas fa-cloud-upload-alt', label: 'Backups' },
    { to: '/system-monitor', icon: 'fas fa-server', label: 'System Monitor' },
  ];
  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${className}`}>
      <div className="sidebar-header">
        <img src="/logo.png" alt="OptiPrime Logo" className="sidebar-logo" />
        {!isCollapsed && (
        <span className="sidebar-brand">
          <span className="opti">Opti</span>
          <span className="prime">Prime</span>
        </span>
        )}
      </div>
      
      {/* Floating Toggle Button on Right Edge */}
      <button 
        className="sidebar-toggle"
        onClick={() => {
          if (isMobile && onMobileToggle) {
            onMobileToggle();
          } else {
            setIsCollapsed(!isCollapsed);
          }
        }}
        title={isMobile ? 'Toggle Menu' : (isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar')}
      >
        <i className={`fas ${isMobile ? 'fa-bars' : `fa-chevron-${isCollapsed ? 'right' : 'left'}`}`}></i>
      </button>
      <nav className="sidebar-nav">
        {navItems.map(item => (
          <div key={item.to}>
          <Link
            to={item.to}
            className={`nav-item${location.pathname.startsWith(item.to) ? ' active' : ''}`}
            title={isCollapsed ? item.label : ''}
          >
            <i className={item.icon}></i>
            {!isCollapsed && <span>{item.label}</span>}
            </Link>
          </div>
        ))}
      </nav>
    </div>
  );
}

function UsersPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);

  // Pending signups state
  const [pendingSignups, setPendingSignups] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingActionLoading, setPendingActionLoading] = useState({});
  const [showPending, setShowPending] = useState(true);

  // Approval modal state
  const [approvalSignup, setApprovalSignup] = useState(null);
  const [approvalWidgetCatalog, setApprovalWidgetCatalog] = useState([]);
  const [approvalSelectedWidgets, setApprovalSelectedWidgets] = useState([]);
  const [approvalForm, setApprovalForm] = useState({});
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalError, setApprovalError] = useState('');

  // Audit logging state
  const [auditData, setAuditData] = useState({});
  const [auditLoading, setAuditLoading] = useState({});
  const [activeAuditTab, setActiveAuditTab] = useState({});

  // Sorting/filtering state
  const [filterCountry, setFilterCountry] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterBuilding, setFilterBuilding] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOnlineStatus, setFilterOnlineStatus] = useState('');
  const [filterTier, setFilterTier] = useState('');

  // Dropdown options
  const [countryOptions, setCountryOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);

  // Theme state
  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme ? 'dark' : 'light');
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const fetchUsers = () => {
    setLoading(true);
    setError('');
    fetch(`${API_URL}/api/community/users`, { headers: { ...adminSessionHeaders() } })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch users');
        return res.json();
      })
      .then(data => {
        setAllUsers(data);
        setError('');
        // Populate filter dropdowns
        setCountryOptions([...new Set(data.map(u => u.country).filter(Boolean))].sort());
        setLanguageOptions([...new Set(data.map(u => u.language).filter(Boolean))].sort());
        // Roles and buildings from buildingRoles
        const allRoles = new Set();
        const allBuildings = new Set();
        data.forEach(u => {
          if (u.buildingRoles) {
            u.buildingRoles.forEach(br => {
              allBuildings.add(br.buildingName);
              br.roles.forEach(r => allRoles.add(r.name));
            });
          }
        });
        setRoleOptions([...allRoles].sort());
        setBuildingOptions([...allBuildings].sort());
      })
      .catch(err => {
        setError('Could not load users.');
        setAllUsers([]);
      })
      .finally(() => setLoading(false));
  };

  const isUserTermsCompliant = (u) => {
    const ok = (v) =>
      v === true ||
      v === 1 ||
      v === '1' ||
      (typeof v === 'string' && v.toLowerCase() === 'true') ||
      (typeof v === 'string' && v.toLowerCase() === 't');
    return ok(u?.termsAccepted) && ok(u?.betaDisclaimerAccepted);
  };

  const fetchPendingSignups = () => {
    setPendingLoading(true);
    fetch(`${API_URL}/api/community/pending-signups`, { headers: { ...adminSessionHeaders() } })
      .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed')))
      .then(data => setPendingSignups(data))
      .catch(() => setPendingSignups([]))
      .finally(() => setPendingLoading(false));
  };

  const handleApproveSignup = (signup) => {
    const nameParts = (signup.full_name || '').trim().split(/\s+/);
    const firstname = nameParts[0] || '';
    const lastname = nameParts.slice(1).join(' ') || '';
    const defaultUsername = signup.email.split('@')[0].replace(/[^a-zA-Z0-9._-]/g, '').toLowerCase();

    setApprovalForm({
      username: defaultUsername,
      firstname,
      lastname,
      email: signup.email,
      telNo: signup.phone || '',
      countryCode: '',
      country: '',
      language: 'en',
    });
    setApprovalSelectedWidgets([]);
    setApprovalError('');
    setApprovalSignup(signup);

    // Fetch the widget catalog for the approval modal
    fetch('/api/community/widgets', { headers: { ...adminSessionHeaders() } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setApprovalWidgetCatalog(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  const handleSubmitApproval = async () => {
    if (!approvalForm.username) { setApprovalError('Username is required'); return; }
    setApprovalLoading(true);
    setApprovalError('');
    setPendingActionLoading(prev => ({ ...prev, [approvalSignup.id]: 'approve' }));
    try {
      const res = await fetch(`${API_URL}/api/community/pending-signups/${approvalSignup.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminSessionHeaders() },
        body: JSON.stringify({ ...approvalForm, widgetIds: approvalSelectedWidgets }),
      });
      const data = await res.json();
      if (res.ok) {
        setApprovalSignup(null);
        alert(data.inviteSent
          ? `User "${approvalForm.username}" created.\n\nAn invite to set their password was emailed to ${approvalForm.email}.`
          : `User "${approvalForm.username}" created, but the invite email failed: ${data.inviteError || 'unknown error'}.\n\nOpen Edit User and click Send invite.`);
        fetchPendingSignups();
        fetchUsers();
      } else {
        setApprovalError(data.error || 'Failed to approve');
      }
    } catch {
      setApprovalError('Network error while approving.');
    } finally {
      setApprovalLoading(false);
      setPendingActionLoading(prev => ({ ...prev, [approvalSignup?.id]: null }));
    }
  };

  const handleRejectSignup = async (signup) => {
    if (!window.confirm(`Reject application from ${signup.full_name}?`)) return;
    const sendEmail = window.confirm('Send rejection notification email?');

    setPendingActionLoading(prev => ({ ...prev, [signup.id]: 'reject' }));
    try {
      const res = await fetch(`${API_URL}/api/community/pending-signups/${signup.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminSessionHeaders() },
        body: JSON.stringify({ sendEmail }),
      });
      if (res.ok) {
        fetchPendingSignups();
      } else {
        const data = await res.json();
        alert('Error: ' + (data.error || 'Failed to reject'));
      }
    } catch {
      alert('Network error while rejecting.');
    } finally {
      setPendingActionLoading(prev => ({ ...prev, [signup.id]: null }));
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPendingSignups();
  }, []);

  function handleResultSelect(user) {
    setSelectedUser(user);
    setIsNewUser(false);
    setIsModalOpen(true);
  }

  function handleEditClick(user) {
    setSelectedUser(user);
    setIsNewUser(false);
    setIsModalOpen(true);
  }

  function handleAddClick() {
    setSelectedUser(null);
    setIsNewUser(true);
    setIsModalOpen(true);
  }

  function handleSaveUser(savedUser) {
    fetchUsers();
    if (savedUser && savedUser.__deferModalClose) {
      return;
    }
    if (savedUser && savedUser.__refreshOnly) {
      return;
    }
    setIsModalOpen(false);
  }

  // Helper to render building assignments as list items with badges
  function renderBuildingAssignments(user) {
    if (!user.buildingRoles || user.buildingRoles.length === 0) {
      return <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', padding: '8px 0' }}>No buildings assigned</div>;
    }
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {user.buildingRoles.map((br) => (
          <li key={user.id + '-building-' + br.buildingId} style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', marginRight: 12, minWidth: 120, display: 'inline-block' }}>
              <i className="fas fa-building" style={{ marginRight: 6, color: 'var(--primary-color)' }}></i>
              {br.buildingName}:
            </span>
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {br.roles.map(r => (
                <span key={r.id || r} style={{
                  background: 'var(--primary-light)',
                  color: 'var(--primary-color)',
                  borderRadius: '12px',
                  padding: '2px 10px',
                  fontSize: '0.95em',
                  fontWeight: 500,
                  border: '1px solid var(--primary-color)',
                  marginRight: 4
                }}>{r.name || r}</span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  // Fetch audit data for a user
  async function fetchAuditData(userId, dataType) {
    try {
      setAuditLoading(prev => ({ ...prev, [`${userId}-${dataType}`]: true }));
      
      const endpoints = {
        loginHistory: `/api/audit/users/${userId}/login-history?limit=20`,
        activitySummary: `/api/audit/users/${userId}/activity-summary?days=30`,
        activeSessions: `/api/audit/users/${userId}/active-sessions`
      };

             // Use proxy endpoints in OptiQuery backend
       const response = await fetch(`${endpoints[dataType]}`, {
         headers: {
           'Content-Type': 'application/json'
         },
         credentials: 'include'
       });

      if (response.ok) {
        const data = await response.json();
        setAuditData(prev => ({
          ...prev,
          [`${userId}-${dataType}`]: data.data
        }));
      } else {
        console.error(`Failed to fetch ${dataType}:`, response.statusText);
      }
    } catch (error) {
      console.error(`Error fetching ${dataType}:`, error);
    } finally {
      setAuditLoading(prev => ({ ...prev, [`${userId}-${dataType}`]: false }));
    }
  }

  // Terminate user session
  async function terminateSession(sessionId, username) {
    if (!window.confirm(`Are you sure you want to terminate the session for ${username}?`)) {
      return;
    }

         try {
       // Use proxy endpoints in OptiQuery backend
       const response = await fetch(`/api/audit/sessions/${sessionId}/terminate`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json'
         },
         credentials: 'include',
         body: JSON.stringify({ reason: 'admin_terminated' })
       });

      if (response.ok) {
        alert('Session terminated successfully');
        // Refresh active sessions for this user
        const userId = expandedUserId;
        if (userId) {
          fetchAuditData(userId, 'activeSessions');
        }
      } else {
        alert('Failed to terminate session');
      }
    } catch (error) {
      console.error('Error terminating session:', error);
      alert('Error terminating session');
    }
  }

  // Format timestamp for display
  function formatTimestamp(timestamp) {
    return new Date(timestamp).toLocaleString();
  }

  // Effect to load audit data when user expands or tab changes
  React.useEffect(() => {
    if (expandedUserId) {
      const currentTab = activeAuditTab[expandedUserId] || 'loginHistory';
      fetchAuditData(expandedUserId, currentTab);
    }
  }, [expandedUserId, activeAuditTab]);

  // Render audit section with tabs
  function renderAuditSection(user) {
    const userId = user.id;
    const currentTab = activeAuditTab[userId] || 'loginHistory';

    const tabs = [
      { id: 'loginHistory', label: 'Login History', icon: 'fas fa-sign-in-alt' },
      { id: 'activitySummary', label: 'Activity Summary', icon: 'fas fa-chart-line' },
      { id: 'activeSessions', label: 'Active Sessions', icon: 'fas fa-user-clock' }
    ];

    return (
      <div>
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '2px solid var(--border-color)',
          marginBottom: 16,
          gap: 4
        }}>
                     {tabs.map(tab => (
             <button
               key={tab.id}
               onClick={() => {
                 setActiveAuditTab(prev => ({ ...prev, [userId]: tab.id }));
                 fetchAuditData(userId, tab.id);
               }}
               style={{
                padding: '8px 16px',
                border: 'none',
                background: currentTab === tab.id ? 'var(--primary-color)' : 'transparent',
                color: currentTab === tab.id ? 'white' : 'var(--text-secondary)',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.2s ease'
              }}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ minHeight: 200 }}>
          {auditLoading[`${userId}-${currentTab}`] ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 40,
              color: 'var(--text-secondary)'
            }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, marginBottom: 8 }}></i>
              <div>Loading audit data...</div>
            </div>
          ) : (
            <>
              {currentTab === 'loginHistory' && renderLoginHistory(userId)}
              {currentTab === 'activitySummary' && renderActivitySummary(userId)}
              {currentTab === 'activeSessions' && renderActiveSessions(userId)}
            </>
          )}
        </div>
      </div>
    );
  }

  // Render login history table
  function renderLoginHistory(userId) {
    const data = auditData[`${userId}-loginHistory`] || [];
    
    if (data.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
          <i className="fas fa-history" style={{ fontSize: 24, marginBottom: 8 }}></i>
          <div>No login history found</div>
        </div>
      );
    }

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surface-color)' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Event</th>
              <th style={{ padding: 8, textAlign: 'left' }}>IP Address</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Timestamp</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((entry, index) => (
              <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className={`fas ${
                      entry.auth_event === 'login_success' ? 'fa-check-circle' :
                      entry.auth_event === 'login_failure' ? 'fa-times-circle' :
                      entry.auth_event === 'logout' ? 'fa-sign-out-alt' : 'fa-info-circle'
                    }`} style={{ 
                      color: entry.auth_event === 'login_success' ? '#4caf50' : 
                             entry.auth_event === 'login_failure' ? '#f44336' : '#ff9800'
                    }}></i>
                    {entry.auth_event.replace(/_/g, ' ').toUpperCase()}
                  </div>
                </td>
                <td style={{ padding: 8, fontFamily: 'monospace' }}>{entry.ip_address}</td>
                <td style={{ padding: 8 }}>{formatTimestamp(entry.timestamp)}</td>
                <td style={{ padding: 8 }}>
                  {entry.failure_reason && (
                    <span style={{ 
                      color: '#f44336', 
                      fontSize: 11,
                      background: '#ffebee',
                      padding: '2px 6px',
                      borderRadius: 4
                    }}>
                      {entry.failure_reason}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Render activity summary
  function renderActivitySummary(userId) {
    const data = auditData[`${userId}-activitySummary`] || [];
    
    if (data.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
          <i className="fas fa-chart-bar" style={{ fontSize: 24, marginBottom: 8 }}></i>
          <div>No activity data found</div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
          Activity summary for the last 30 days
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {data.map((activity, index) => (
            <div key={index} style={{
              padding: 16,
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              background: 'var(--surface-color)'
            }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                {activity.activity_type.replace(/_/g, ' ').toUpperCase()}
              </div>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: 'var(--primary-color)', marginBottom: 4 }}>
                {activity.count}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Last: {formatTimestamp(activity.last_activity)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render active sessions
  function renderActiveSessions(userId) {
    const data = auditData[`${userId}-activeSessions`] || [];
    
    if (data.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
          <i className="fas fa-user-slash" style={{ fontSize: 24, marginBottom: 8 }}></i>
          <div>No active sessions</div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
          Currently active sessions for this user
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--surface-color)' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>IP Address</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Login Time</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Last Activity</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Idle Time</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((session, index) => (
                <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: 8, fontFamily: 'monospace' }}>{session.ip_address}</td>
                  <td style={{ padding: 8 }}>{formatTimestamp(session.login_time)}</td>
                  <td style={{ padding: 8 }}>{formatTimestamp(session.last_activity)}</td>
                  <td style={{ padding: 8 }}>
                    <span style={{
                      color: session.minutes_idle > 30 ? '#f44336' : 
                             session.minutes_idle > 15 ? '#ff9800' : '#4caf50',
                      fontWeight: 500
                    }}>
                      {session.minutes_idle} min
                    </span>
                  </td>
                  <td style={{ padding: 8 }}>
                    <button
                      onClick={() => terminateSession(session.session_id, session.username)}
                      style={{
                        padding: '4px 8px',
                        fontSize: 11,
                        border: '1px solid #f44336',
                        background: '#fff',
                        color: '#f44336',
                        borderRadius: 4,
                        cursor: 'pointer'
                      }}
                    >
                      <i className="fas fa-times"></i> Terminate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function handleExpand(userId) {
    setExpandedUserId(prev => (prev === userId ? null : userId));
  }

  /** BETA Users page lists all enabled community accounts; grey dot = never logged in yet. */
  function isTruthyDbFlag(v) {
    if (v === true || v === 1 || v === '1') return true;
    if (typeof v === 'string' && v.toLowerCase() === 't') return true;
    return false;
  }

  function isBetaCommunityUser(user) {
    if (!isTruthyDbFlag(user.isActive) || !isTruthyDbFlag(user.accountEnabled)) return false;
    const un = String(user.username || '').toLowerCase();
    if (un === 'optiprime.system' || un.endsWith('.system')) return false;
    return true;
  }

  function hasLoggedInAtLeastOnce(user) {
    if (user.lastLogin == null || String(user.lastLogin).trim() === '') return false;
    return !Number.isNaN(new Date(user.lastLogin).getTime());
  }

  /** True when user has a non-expired row in PostgreSQL user_sessions (OptiPrime app login). */
  function userIsCurrentlyLoggedIn(user) {
    return user.isLoggedIn === true;
  }

  /** Human-readable "X minutes/days ago" for a past ISO timestamp. */
  function formatRelativePast(isoDate) {
    if (isoDate == null || String(isoDate).trim() === '') return '';
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return '';
    let sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 45) return 'just now';
    if (sec < 3600) {
      const m = Math.max(1, Math.floor(sec / 60));
      return `${m} minute${m === 1 ? '' : 's'} ago`;
    }
    if (sec < 86400) {
      const h = Math.floor(sec / 3600);
      return `${h} hour${h === 1 ? '' : 's'} ago`;
    }
    const days = Math.floor(sec / 86400);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
    const years = Math.floor(days / 365);
    return `${years} year${years === 1 ? '' : 's'} ago`;
  }

  function presenceStatusTitle(user) {
    if (!hasLoggedInAtLeastOnce(user)) {
      return 'Never logged in — no activity recorded';
    }
    if (userIsCurrentlyLoggedIn(user)) {
      const sess = formatRelativePast(user.sessionLastActivity);
      return sess
        ? `Online — currently logged in (session activity ${sess})`
        : 'Online — currently logged in';
    }
    const last = formatRelativePast(user.lastLogin);
    return last ? `Not logged in — last active ${last}` : 'Not logged in — last active time unknown';
  }

  /** OPTI tier from feedback_reputation — same cutoffs as app dashboard (1 / 5 / 25 / 100). */
  function getOptiTierLabel(user) {
    const score = Number(user?.feedbackReputation ?? user?.feedback_reputation ?? 0) || 0;
    if (score >= 100) return 'Diamond';
    if (score >= 25) return 'Platinum';
    if (score >= 5) return 'Gold';
    if (score >= 1) return 'Silver';
    return 'Bronze';
  }

  function optiTierBadgeColors(tier) {
    const map = {
      Bronze: { bg: 'rgba(205, 127, 50, 0.22)', color: '#CD7F32', border: '#b45309' },
      Silver: { bg: 'rgba(148, 163, 184, 0.25)', color: '#e2e8f0', border: '#94a3b8' },
      Gold: { bg: 'rgba(234, 179, 8, 0.2)', color: '#fde047', border: '#ca8a04' },
      Platinum: { bg: 'rgba(226, 232, 240, 0.18)', color: '#f1f5f9', border: '#cbd5e1' },
      Diamond: { bg: 'rgba(34, 211, 238, 0.18)', color: '#a5f3fc', border: '#22d3ee' },
    };
    return map[tier] || map.Bronze;
  }

  const betaCommunityUsers = allUsers.filter(isBetaCommunityUser);
  const usersWithLogin = betaCommunityUsers.filter(hasLoggedInAtLeastOnce);

  const filteredUsers = betaCommunityUsers.filter(user => {
    if (filterCountry && user.country !== filterCountry) return false;
    if (filterLanguage && user.language !== filterLanguage) return false;
    if (filterStatus !== '' && user.accountEnabled !== parseInt(filterStatus)) return false;
    if (filterBuilding) {
      if (!user.buildingRoles || !user.buildingRoles.some(br => br.buildingName === filterBuilding)) return false;
    }
    if (filterRole) {
      if (!user.buildingRoles || !user.buildingRoles.some(br => br.roles.some(r => r.name === filterRole))) return false;
    }
    if (filterOnlineStatus !== '') {
      const isOn = userIsCurrentlyLoggedIn(user);
      if (filterOnlineStatus === 'online' && !isOn) return false;
      if (filterOnlineStatus === 'offline' && isOn) return false;
    }
    if (filterTier && getOptiTierLabel(user) !== filterTier) return false;
    return true;
  });

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>👥 BETA Users</h1>
            </div>
            <div className="top-nav-actions">
              <button 
                className="icon-button settings-button" 
                onClick={() => {
                  const settingsEvent = new CustomEvent('openAdminSettings');
                  window.dispatchEvent(settingsEvent);
                }}
                title="Admin Settings"
              >
                <i className="fas fa-cog"></i>
              </button>
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button 
                className="icon-button logout-button" 
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">

          {/* Pending BETA Applications */}
          {(() => {
            const pendingOnly = pendingSignups.filter(s => s.status === 'pending');
            if (pendingOnly.length === 0 && !pendingLoading) return null;
            return (
              <div className="card" style={{ marginBottom: '20px', border: '2px solid #f59e0b', borderRadius: '12px' }}>
                <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowPending(p => !p)}>
                  <div className="header-title-section" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h2 style={{ margin: 0 }}>Pending BETA Applications</h2>
                    <span style={{
                      background: '#f59e0b', color: '#fff', borderRadius: '12px', padding: '2px 10px',
                      fontWeight: 700, fontSize: '14px', minWidth: '24px', textAlign: 'center',
                    }}>{pendingOnly.length}</span>
                    <i className={`fas fa-chevron-${showPending ? 'up' : 'down'}`} style={{ color: '#888', marginLeft: '8px' }}></i>
                  </div>
                </div>
                {showPending && (
                  <div className="card-content">
                    {pendingLoading && <div className="loading-spinner">Loading applications...</div>}
                    {pendingOnly.length > 0 && (
                      <table className="modern-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Interest</th>
                            <th>Service</th>
                            <th>Source</th>
                            <th>Applied</th>
                            <th>Terms</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingOnly.map(s => (
                            <tr key={s.id}>
                              <td style={{ fontWeight: 600 }}>{s.full_name}</td>
                              <td>{s.email}</td>
                              <td>{s.phone || '-'}</td>
                              <td>{s.role_interest || '-'}</td>
                              <td>{s.service_interest || '-'}</td>
                              <td><span style={{ fontSize: '12px', background: 'rgba(101,234,239,0.12)', color: '#c8e6f0', border: '1px solid rgba(101,234,239,0.25)', borderRadius: '4px', padding: '2px 6px' }}>{s.source || '?'}</span></td>
                              <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{new Date(s.created_at).toLocaleDateString()}</td>
                              <td style={{ textAlign: 'center' }}>
                                {s.terms_accepted ? <span style={{ color: '#059669' }}>Yes</span> : <span style={{ color: '#dc2626' }}>No</span>}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <button
                                  className="btn btn-primary"
                                  style={{ marginRight: '6px', padding: '6px 12px', fontSize: '13px' }}
                                  disabled={!!pendingActionLoading[s.id]}
                                  onClick={() => handleApproveSignup(s)}
                                >
                                  {pendingActionLoading[s.id] === 'approve' ? 'Approving...' : 'Approve'}
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  style={{ padding: '6px 12px', fontSize: '13px', color: '#dc2626', borderColor: '#dc2626' }}
                                  disabled={!!pendingActionLoading[s.id]}
                                  onClick={() => handleRejectSignup(s)}
                                >
                                  {pendingActionLoading[s.id] === 'reject' ? 'Rejecting...' : 'Reject'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ─── Tactical hero + KPI strip ─────────────────────────────── */}
          {(() => {
            const totalListedUsers = betaCommunityUsers.length;
            const onlineUsers = betaCommunityUsers.filter(userIsCurrentlyLoggedIn);
            const enabledUsers = betaCommunityUsers.filter((u) => u.accountEnabled);
            const disabledUsers = betaCommunityUsers.filter((u) => !u.accountEnabled);
            const compliantUsers = betaCommunityUsers.filter(isUserTermsCompliant);
            const pendingApplications = pendingSignups.filter((s) => s.status === 'pending').length;
            const onlinePct = totalListedUsers ? (onlineUsers.length / totalListedUsers) * 100 : 0;
            const enabledPct = totalListedUsers ? (enabledUsers.length / totalListedUsers) * 100 : 0;
            const compliantPct = totalListedUsers ? (compliantUsers.length / totalListedUsers) * 100 : 0;

            let level = 'good';
            let label = 'COMMUNITY HEALTHY';
            if (compliantPct < 50 || disabledUsers.length > totalListedUsers * 0.25) {
              level = 'critical'; label = 'INTERVENTION NEEDED';
            } else if (compliantPct < 80 || pendingApplications > 5) {
              level = 'warning'; label = 'REVIEW PENDING';
            }

            return (
              <div className="opo-tactical users-tactical">
                <div className={`opo-hero opo-hero-${level}`}>
                  <div className="opo-hero-scan" aria-hidden="true" />
                  <div className="opo-hero-left">
                    <div className="opo-hero-eyebrow">BETA COMMUNITY STATUS</div>
                    <div className="opo-hero-title">
                      <span className={`opo-hero-pulse opo-pulse-${level}`} />
                      <h2>{label}</h2>
                    </div>
                    <div className="opo-hero-sub">
                      {totalListedUsers} active accounts · {onlineUsers.length} online now · {pendingApplications} pending application{pendingApplications === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="opo-hero-right">
                    <button
                      type="button"
                      className="opo-refresh-btn"
                      onClick={() => { fetchUsers(); fetchPendingSignups(); }}
                      title="Re-fetch users and pending signups"
                    >
                      <span className="opo-refresh-icon" /> SYNC
                    </button>
                    <div className="opo-hero-timestamp">
                      {new Date().toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="opo-kpi-row">
                  <div className="opo-kpi">
                    <div className="opo-kpi-label">REGISTERED USERS</div>
                    <div className="opo-kpi-value opo-mono-value">{totalListedUsers.toLocaleString()}</div>
                    <div className="opo-kpi-foot">shown: {filteredUsers.length.toLocaleString()} · community: {betaCommunityUsers.length.toLocaleString()} · logged in: {usersWithLogin.length.toLocaleString()}</div>
                  </div>
                  <div className="opo-kpi">
                    <div className="opo-kpi-label">LOGGED IN NOW</div>
                    <div className="opo-kpi-value opo-mono-value opo-level-good">{onlineUsers.length}</div>
                    <div className="opo-bar-track opo-bar-mini">
                      <div className="opo-bar-fill opo-fill-good" style={{ width: `${onlinePct}%` }} />
                    </div>
                    <div className="opo-kpi-foot">{onlinePct.toFixed(0)}% of community</div>
                  </div>
                  <div className="opo-kpi">
                    <div className="opo-kpi-label">ACCOUNTS ENABLED</div>
                    <div className={`opo-kpi-value opo-mono-value opo-level-${enabledPct >= 90 ? 'good' : enabledPct >= 70 ? 'warning' : 'critical'}`}>
                      {enabledUsers.length}<span className="opo-kpi-sep">/</span>{totalListedUsers}
                    </div>
                    <div className="opo-bar-track opo-bar-mini">
                      <div
                        className={`opo-bar-fill opo-fill-${enabledPct >= 90 ? 'good' : enabledPct >= 70 ? 'warning' : 'critical'}`}
                        style={{ width: `${enabledPct}%` }}
                      />
                    </div>
                    <div className="opo-kpi-foot">{disabledUsers.length} disabled</div>
                  </div>
                  <div className="opo-kpi">
                    <div className="opo-kpi-label">COMPLIANCE</div>
                    <div className={`opo-kpi-value opo-mono-value opo-level-${compliantPct >= 80 ? 'good' : compliantPct >= 50 ? 'warning' : 'critical'}`}>
                      {compliantPct.toFixed(0)}%
                    </div>
                    <div className="opo-bar-track opo-bar-mini">
                      <div
                        className={`opo-bar-fill opo-fill-${compliantPct >= 80 ? 'good' : compliantPct >= 50 ? 'warning' : 'critical'}`}
                        style={{ width: `${compliantPct}%` }}
                      />
                    </div>
                    <div className="opo-kpi-foot">terms + BETA disclaimer</div>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="card users-card opo-tactical users-list-card">
            <div className="card-header users-list-card-header">
              <div className="header-title-section">
                <h2 className="users-list-title">
                  <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
                  ALL USERS
                  <span className="users-list-count opo-mono-value">{filteredUsers.length}</span>
                </h2>
              </div>
              <div className="card-actions">
                <button className="btn btn-primary" onClick={handleAddClick}>
                  <i className="fas fa-plus"></i> Add User
                </button>
              </div>
            </div>
            {/* Sorting/Filtering Tool */}
            <div className="users-filter-bar" style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '18px 0 10px 0', flexWrap: 'wrap' }}>
              <span className="users-filter-tag">FILTERS</span>
              <div>
                <label style={{ fontWeight: 500, marginRight: 6 }}>Country:</label>
                <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
                  <option value="">All</option>
                  {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 500, marginRight: 6 }}>Role:</label>
                <select value={filterRole} onChange={e => setFilterRole(e.target.value)}>
                  <option value="">All</option>
                  {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 500, marginRight: 6 }}>Building:</label>
                <select value={filterBuilding} onChange={e => setFilterBuilding(e.target.value)}>
                  <option value="">All</option>
                  {buildingOptions.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 500, marginRight: 6 }}>Account:</label>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="">All</option>
                  <option value="1">Enabled</option>
                  <option value="0">Disabled</option>
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 500, marginRight: 6 }}>Status:</label>
                <select value={filterOnlineStatus} onChange={e => setFilterOnlineStatus(e.target.value)}>
                  <option value="">All</option>
                  <option value="online">Logged in now</option>
                  <option value="offline">Not logged in</option>
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 500, marginRight: 6 }}>Language:</label>
                <select value={filterLanguage} onChange={e => setFilterLanguage(e.target.value)}>
                  <option value="">All</option>
                  {languageOptions.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 500, marginRight: 6 }}>Tier:</label>
                <select value={filterTier} onChange={e => setFilterTier(e.target.value)}>
                  <option value="">All</option>
                  <option value="Bronze">Bronze</option>
                  <option value="Silver">Silver</option>
                  <option value="Gold">Gold</option>
                  <option value="Platinum">Platinum</option>
                  <option value="Diamond">Diamond</option>
                </select>
              </div>
              <button className="btn btn-secondary" style={{ marginLeft: 8 }} onClick={() => {
                setFilterCountry(''); setFilterRole(''); setFilterBuilding(''); setFilterLanguage(''); setFilterStatus(''); setFilterOnlineStatus(''); setFilterTier('');
              }}>Clear Filters</button>
            </div>
            <div className="card-content">
              {loading && <div className="loading-spinner">Loading users...</div>}
              {error && <div className="error-message">{error}</div>}
              {filteredUsers.length > 0 ? (
                <table className="modern-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Telephone</th>
                      <th>Country</th>
                      <th>Language</th>
                      <th>Compliance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <React.Fragment key={user.id}>
                        <tr
                          data-user-id={user.id}
                          style={{ cursor: 'pointer', background: expandedUserId === user.id ? 'var(--primary-light)' : undefined }}
                          onClick={e => {
                            if (e.target.closest('.icon-button')) return;
                            handleExpand(user.id);
                          }}
                        >
                          <td style={{ width: 132, textAlign: 'center', padding: '8px 4px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                              
                              {/* Top Row: OPTI tier + Status Light + Chevron */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                                {(() => {
                                  const tierLabel = getOptiTierLabel(user);
                                  const tc = optiTierBadgeColors(tierLabel);
                                  const optBal = Number(user?.feedbackReputation ?? user?.feedback_reputation ?? 0) || 0;
                                  return (
                                    <span
                                      title={`OPTI tier — ${optBal.toFixed(2)} OPT (feedback reputation)`}
                                      style={{
                                        fontSize: '9px',
                                        fontWeight: 800,
                                        letterSpacing: '0.03em',
                                        textTransform: 'uppercase',
                                        padding: '3px 5px',
                                        borderRadius: '4px',
                                        border: `1px solid ${tc.border}`,
                                        backgroundColor: tc.bg,
                                        color: tc.color,
                                        maxWidth: '56px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {tierLabel}
                                    </span>
                                  );
                                })()}
                                {/* Green = valid OptiPrime app session; red = no active session; grey = never logged in */}
                                <div 
                                  className="status-indicator"
                                  style={{
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    backgroundColor: (() => {
                                      if (!hasLoggedInAtLeastOnce(user)) return '#64748b';
                                      return userIsCurrentlyLoggedIn(user) ? '#00ff41' : '#ff1744';
                                    })(),
                                    boxShadow: (() => {
                                      if (!hasLoggedInAtLeastOnce(user)) return '0 0 12px rgba(100, 116, 139, 1), inset 0 2px 4px rgba(255, 255, 255, 0.2)';
                                      return userIsCurrentlyLoggedIn(user)
                                        ? '0 0 16px rgba(0, 255, 65, 1), inset 0 2px 4px rgba(255, 255, 255, 0.3)'
                                        : '0 0 16px rgba(255, 23, 68, 1), inset 0 2px 4px rgba(255, 255, 255, 0.3)';
                                    })(),
                                    border: '2px solid rgba(255, 255, 255, 0.4)',
                                    flexShrink: 0,
                                    filter: (() => {
                                      if (!hasLoggedInAtLeastOnce(user)) return 'none';
                                      return userIsCurrentlyLoggedIn(user)
                                        ? 'drop-shadow(0 0 6px #00ff41)'
                                        : 'drop-shadow(0 0 6px #ff1744)';
                                    })()
                                  }}
                                  title={presenceStatusTitle(user)}
                                ></div>
                                
                                {/* Expand Arrow */}
                                <i 
                                  className={`fas fa-chevron-${expandedUserId === user.id ? 'down' : 'right'}`} 
                                  style={{ 
                                    color: 'var(--primary-color)', 
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                  }}
                                ></i>
                              </div>
                              
                              {/* Aesthetic Dividing Line */}
                              <div style={{
                                width: '60px',
                                height: '1px',
                                background: 'linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.4), transparent)',
                                margin: '2px 0'
                              }}></div>
                              
                              {/* Bottom: Account Enabled/Disabled Tablet */}
                              <div style={{
                                padding: '3px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                backgroundColor: user.accountEnabled ? '#064e3b' : '#7f1d1d',
                                color: user.accountEnabled ? '#6ee7b7' : '#fca5a5',
                                border: `1px solid ${user.accountEnabled ? '#059669' : '#dc2626'}`,
                                minWidth: '56px',
                                textAlign: 'center',
                                textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                              }}>
                                {user.accountEnabled ? 'Enabled' : 'Disabled'}
                              </div>
                            </div>
                          </td>
                          <td>{user.firstName} {user.lastName}</td>
                          <td>{user.username}</td>
                          <td>{user.email}</td>
                          <td>{user.telNo}</td>
                          <td>{user.country}</td>
                          <td>{user.language || '-'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                            }}>
                              {(() => {
                                const compliant = isUserTermsCompliant(user);
                                return (
                                  <>
                                    <div
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: compliant ? '#059669' : '#dc2626',
                                        border: compliant ? '2px solid #047857' : '2px solid #991b1b',
                                        cursor: 'default',
                                      }}
                                      title={
                                        compliant
                                          ? 'Terms of Service and BETA disclaimer accepted'
                                          : 'Missing terms and/or BETA disclaimer acceptance'
                                      }
                                    />
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        color: compliant ? '#059669' : '#dc2626',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {compliant ? 'Compliant' : 'Non-Compliant'}
                                    </span>
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                          <td>
                            <button 
                              className="icon-button" 
                              title="Edit User"
                              onClick={e => { e.stopPropagation(); handleEditClick(user); }}
                              style={{ fontSize: 20 }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                          </td>
                        </tr>
                        {expandedUserId === user.id && (
                          <tr>
                            <td colSpan={9} style={{ background: 'var(--surface-color)', borderTop: '1px solid var(--border-color)', padding: 0 }}>
                              <div style={{ padding: '18px 32px', minHeight: 48 }}>
                                <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Building Assignments</div>
                                {renderBuildingAssignments(user)}

                                {/* Lifecycle view: how far this person has actually got.
                                    The audit card below answers a different question — who
                                    logged in from where — so it stays empty until they do. */}
                                <UserJourneyTimeline userId={user.id} />

                                {/* Government-Grade Audit Logging Section */}
                                <div style={{ 
                                  marginTop: 24, 
                                  padding: '16px', 
                                  border: '1px solid var(--border-color)', 
                                  borderRadius: 8, 
                                  background: 'var(--card-background)' 
                                }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    marginBottom: 12, 
                                    color: 'var(--text-primary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                  }}>
                                    <i className="fas fa-shield-alt" style={{ color: 'var(--primary-color)' }}></i>
                                    Security & Audit Logs
                                    <span style={{
                                      background: '#e8f5e8',
                                      color: '#2d7d32',
                                      padding: '2px 8px',
                                      borderRadius: 12,
                                      fontSize: 11,
                                      fontWeight: 500
                                    }}>
                                      GOVERNMENT GRADE
                                    </span>
                                  </div>
                                  {renderAuditSection(user)}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', padding: '18px 0' }}>
                  {betaCommunityUsers.length === 0 && allUsers.length > 0
                    ? 'No enabled BETA community accounts to show.'
                    : 'No users found for the selected filters.'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <UserModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveUser}
        isNewUser={isNewUser}
      />

      {/* Approval Modal - opened when admin clicks "Approve" on a pending signup */}
      {approvalSignup && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-content" style={{ maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2><i className="fas fa-user-check" style={{ marginRight: 10 }}></i>Approve BETA Application</h2>
              <button className="close-button" onClick={() => setApprovalSignup(null)}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              {/* Application Details (read-only summary) */}
              <div style={{ background: 'var(--input-bg, #0f172a)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 16, marginBottom: 18 }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1 }}>Application Details</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', fontSize: 14 }}>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Name:</span> <strong>{approvalSignup.full_name}</strong></div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Email:</span> <strong>{approvalSignup.email}</strong></div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Phone:</span> {approvalSignup.phone || '—'}</div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Source:</span> {approvalSignup.source || '—'}</div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Role interest:</span> {approvalSignup.role_interest || '—'}</div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Service interest:</span> {approvalSignup.service_interest || '—'}</div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Terms accepted:</span> {approvalSignup.terms_accepted ? '✅ Yes' : '❌ No'}</div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>BETA disclaimer:</span> {approvalSignup.beta_disclaimer_accepted ? '✅ Yes' : '❌ No'}</div>
                </div>
                {approvalSignup.message && (
                  <div style={{ marginTop: 10 }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Message:</span>
                    <p style={{ margin: '4px 0 0', fontSize: 13, fontStyle: 'italic', color: 'var(--text-primary)' }}>{approvalSignup.message}</p>
                  </div>
                )}
              </div>

              {/* Editable user fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                {[
                  { key: 'firstname', label: 'First Name *' },
                  { key: 'lastname', label: 'Last Name *' },
                  { key: 'username', label: 'Username *' },
                  { key: 'email', label: 'Email *' },
                  { key: 'telNo', label: 'Phone' },
                  { key: 'countryCode', label: 'Country Code' },
                  { key: 'country', label: 'Country' },
                ].map(({ key, label }) => (
                  <div className="form-group" key={key} style={{ margin: 0 }}>
                    <label style={{ fontSize: 12 }}>{label}</label>
                    <input
                      type="text"
                      value={approvalForm[key] || ''}
                      onChange={e => setApprovalForm(prev => ({ ...prev, [key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>

              {/* Widget Permissions */}
              <div style={{ marginBottom: 18, padding: 16, background: 'var(--card-bg, #1e293b)', border: '1px solid var(--border-color)', borderRadius: 10 }}>
                <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                  <i className="fas fa-th-large" style={{ marginRight: 8, color: 'var(--accent, #2563eb)' }}></i>Widget Permissions
                </h4>
                {approvalWidgetCatalog.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading widgets…</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
                    {approvalWidgetCatalog.map(w => {
                      const checked = approvalSelectedWidgets.includes(w.id);
                      return (
                        <label
                          key={w.id}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                            padding: '10px 12px', borderRadius: 8,
                            background: checked ? 'rgba(37,99,235,0.12)' : 'var(--input-bg, #0f172a)',
                            border: `1px solid ${checked ? 'var(--accent, #2563eb)' : 'var(--border-color, #334155)'}`,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => setApprovalSelectedWidgets(prev =>
                              prev.includes(w.id) ? prev.filter(x => x !== w.id) : [...prev, w.id]
                            )}
                            style={{ marginTop: 2 }}
                          />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
                              <i className={`fas ${w.iconName || 'fa-puzzle-piece'}`} style={{ marginRight: 6, color: 'var(--accent, #2563eb)' }}></i>
                              {w.name}
                            </div>
                            {w.status === 'coming_soon' && (
                              <span style={{ fontSize: 10, color: '#d97706', fontWeight: 600 }}>Coming soon</span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '12px 0 0' }}>
                  Selected widgets will be allocated to the user and included in the welcome email.
                </p>
              </div>

              {approvalError && (
                <div style={{ color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 14 }}>
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>{approvalError}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--border-color)' }}>
              <button className="btn btn-secondary" onClick={() => setApprovalSignup(null)} disabled={approvalLoading}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSubmitApproval}
                disabled={approvalLoading}
              >
                {approvalLoading ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Approving…</> : <><i className="fas fa-user-check" style={{ marginRight: 8 }}></i>Approve &amp; Send Invite</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ThemeContext.Provider>
  );
}

/** Parse service_packages.features — stored as JSON text; corrupt saves become "[object Object]"; some stacks may return an object. */
function PackageManagementPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/service-packages`);
      const data = await response.json();
      setPackages(data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setSelectedPackage(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (pkg) => {
    setSelectedPackage(pkg);
    setIsModalOpen(true);
  };

  const handleSavePackage = async (packageData) => {
    try {
      const url = selectedPackage 
        ? `/api/service-packages/${selectedPackage.id}`
        : '/api/service-packages';
      
      const method = selectedPackage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(packageData),
      });

      if (response.ok) {
        fetchPackages();
        setIsModalOpen(false);
      } else {
        console.error('Error saving package');
      }
    } catch (error) {
      console.error('Error saving package:', error);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        const response = await fetch(`/api/service-packages/${packageId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          fetchPackages();
        } else {
          console.error('Error deleting package');
        }
      } catch (error) {
        console.error('Error deleting package:', error);
      }
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      Bronze: '#CD7F32',
      Silver: '#C0C0C0',
      Gold: '#FFD700',
      Enterprise: '#6366f1',
      Platinum: '#6366f1', /* legacy rows before migration */
    };
    return colors[tier] || '#666';
  };

  // SaaS tier label (DB stores legacy Bronze/Silver/Gold codes for color hooks; the
  // human-facing name is the per-doc role: Entry Level, Professional, …).
  const getTierBadgeLabel = (pkg) => {
    const code = pkg.package_tier;
    if (code === 'Bronze') return 'ENTRY';
    if (code === 'Silver') return 'PROFESSIONAL';
    if (code === 'Gold')   return 'HIGH-VOLUME';
    return (code || '').toUpperCase();
  };

  // Static, non-editable revenue channels documented in
  // Docs/00-CRITICAL/OPTIPRIME_BUSINESS_MODEL_SUMMARY.md (v2.0). Rendered as
  // tactical telemetry tiles next to the editable SaaS subscription tiers.
  const REVENUE_CHANNELS = [
    {
      key: 'strategic-licensing',
      icon: 'fa-handshake',
      tag: 'STRATEGIC LICENSING',
      title: 'Strategic Partner Licensing',
      pricingLine: '$15 / member / yr · or $500K – $1M+ institutional',
      summary: 'Cohort access for partners that already aggregate professional users (CREA, BCREA, RECBC firms, insurers). Co-branded onboarding and Marketing Hub access.',
      bullets: [
        'CREA / realtor.ca cohorts',
        'BCREA + strata associations',
        'E&O insurer co-marketing',
      ],
      level: 'good',
    },
    {
      key: 'enterprise-whitelabel',
      icon: 'fa-building',
      tag: 'ENTERPRISE WHITE-LABEL',
      title: 'OptiWrapper Deployment',
      pricingLine: '$25K – $500K+ setup · $10K – $100K+ / yr SaaS & support',
      summary: 'Productized wrap → demo → sell engagement. Public web mirrored into a controlled OptiPrime portal in 2–4 weeks; transition or licence on approval.',
      bullets: [
        'Full custom OptiWrapper deployment',
        'Hardware management',
        'Outbound demo-link lead engine',
      ],
      level: 'good',
    },
    {
      key: 'optilaw-api',
      icon: 'fa-balance-scale',
      tag: 'API LICENSING',
      title: 'OptiLaw AI API',
      pricingLine: 'High-margin compliance licensing · pricing TBD',
      summary: 'Embedded compliance/legal mapping API for insurers, legal firms, and enterprise platforms. Backed by 78K+ ingested BC legal documents with retrieval-first audit trail.',
      bullets: [
        'Insurers (E&O, regulated lines)',
        'Legal & conveyancing firms',
        'Enterprise integration platforms',
      ],
      level: 'warning',
    },
    {
      key: 'colabs-opti',
      icon: 'fa-users',
      tag: 'RETENTION DRIVEN',
      title: 'Co-Labs + OPTI Ecosystem',
      pricingLine: 'Indirect monetization · loyalty-style participation',
      summary: 'Non-speculative participation & reward layer linked to real-world value. Drives retention, partner rewards, education uplift, and ecosystem growth — feedback-graded into the roadmap.',
      bullets: [
        'Higher retention vs. baseline SaaS',
        'Partner rewards (Aeroplan-style)',
        'Education / training uplift',
      ],
      level: 'good',
    },
  ];

  // Aggregate metrics for the KPI strip — driven entirely by live SaaS rows.
  const activeSaas = packages.filter(p => p.is_active);
  const monthlyMin  = activeSaas.length ? Math.min(...activeSaas.map(p => Number(p.price_monthly) || 0)) : 0;
  const monthlyMax  = activeSaas.length ? Math.max(...activeSaas.map(p => Number(p.price_monthly) || 0)) : 0;
  const yearlyMin   = activeSaas.length ? Math.min(...activeSaas.map(p => Number(p.price_yearly)  || 0)) : 0;
  const yearlyMax   = activeSaas.length ? Math.max(...activeSaas.map(p => Number(p.price_yearly)  || 0)) : 0;
  const totalChannels = REVENUE_CHANNELS.length + 1; // +1 = SaaS itself
  const heroLevel = loading ? 'unknown' : (activeSaas.length >= 3 ? 'good' : activeSaas.length > 0 ? 'warning' : 'critical');
  const heroLabel = loading
    ? 'SYNCING REVENUE MODEL…'
    : heroLevel === 'good'     ? 'REVENUE MODEL ONLINE'
    : heroLevel === 'warning'  ? 'PARTIAL COVERAGE'
    :                            'NO LIVE TIERS';

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-wrapper">
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>Package Management</h1>
            </div>
            <div className="top-nav-actions">
              <button
                className="btn btn-primary"
                onClick={handleAddClick}
              >
                <i className="fas fa-plus"></i> Add SaaS Tier
              </button>
            </div>
          </div>

          <div className="card opo-tactical packages-shell">
            {/* Tactical hero banner */}
            <div className={`opo-hero opo-hero-${heroLevel}`}>
              <div className="opo-hero-scan" aria-hidden="true" />
              <div className="opo-hero-left">
                <div className="opo-hero-eyebrow">REVENUE MODEL · 5 CHANNELS · CAD</div>
                <div className="opo-hero-title">
                  <span className={`opo-hero-pulse opo-pulse-${heroLevel}`} />
                  <h2>{heroLabel}</h2>
                </div>
                <div className="opo-hero-sub">
                  {activeSaas.length} live SaaS tier{activeSaas.length === 1 ? '' : 's'} ·
                  {' '}{REVENUE_CHANNELS.length} strategic channel{REVENUE_CHANNELS.length === 1 ? '' : 's'} ·
                  {' '}early-stage model · refined before market launch
                </div>
              </div>
              <div className="opo-hero-right">
                <button type="button" className="opo-refresh-btn" onClick={fetchPackages}>
                  <span className="opo-refresh-icon" /> SYNC
                </button>
                <div className="opo-hero-timestamp">{new Date().toLocaleString()}</div>
              </div>
            </div>

            {/* KPI strip */}
            <div className="opo-kpi-row">
              <div className="opo-kpi">
                <div className="opo-kpi-label">SAAS TIERS</div>
                <div className="opo-kpi-value opo-mono-value opo-level-good">
                  {activeSaas.length}<span className="opo-kpi-sep"> / </span>{packages.length}
                </div>
                <div className="opo-kpi-foot">live · per-user / month</div>
              </div>
              <div className="opo-kpi">
                <div className="opo-kpi-label">MONTHLY RANGE (CAD)</div>
                <div className="opo-kpi-value opo-mono-value">
                  ${monthlyMin}<span className="opo-kpi-sep"> – </span>${monthlyMax}
                </div>
                <div className="opo-kpi-foot">entry → high-volume</div>
              </div>
              <div className="opo-kpi">
                <div className="opo-kpi-label">ANNUAL PREPAID (CAD)</div>
                <div className="opo-kpi-value opo-mono-value">
                  ${yearlyMin}<span className="opo-kpi-sep"> – </span>${yearlyMax}
                </div>
                <div className="opo-kpi-foot">~15 – 17.5% off monthly</div>
              </div>
              <div className="opo-kpi">
                <div className="opo-kpi-label">REVENUE CHANNELS</div>
                <div className="opo-kpi-value opo-mono-value opo-level-good">
                  {totalChannels}
                </div>
                <div className="opo-kpi-foot">SaaS · partner · enterprise · API · ecosystem</div>
              </div>
            </div>

            {/* SaaS subscription tiers (editable) */}
            <h3 className="opo-section-heading packages-section-heading">
              <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
              SAAS SUBSCRIPTION TIERS
              <span className="opo-mono-value packages-section-count">{activeSaas.length}</span>
            </h3>
            <p className="packages-section-sub">
              Per-user, per-month subscriptions billed monthly · annual prepaid available with discount.
              Source of truth: <code>Docs/00-CRITICAL/OPTIPRIME_BUSINESS_MODEL_SUMMARY.md</code> (v2.0).
            </p>

            {loading ? (
              <div className="packages-empty">
                <i className="fas fa-spinner fa-spin" />
                <p>SYNCING SAAS TIERS…</p>
              </div>
            ) : packages.length === 0 ? (
              <div className="packages-empty">
                <i className="fas fa-box" />
                <p>NO SAAS TIERS DEFINED</p>
                <button className="btn btn-primary" onClick={handleAddClick}>
                  <i className="fas fa-plus"></i> Add First Tier
                </button>
              </div>
            ) : (
              <div className="packages-grid-tactical">
                {packages.map((pkg) => {
                  const tierColor = getTierColor(pkg.package_tier);
                  const features = parseServicePackageFeatures(pkg.features);
                  return (
                    <div key={pkg.id} className={`opo-card-tactical opo-card-good package-card-tactical`}>
                      <div className="package-card-tactical-header">
                        <span
                          className="package-card-tactical-tier"
                          style={{ color: tierColor, borderColor: tierColor }}
                        >
                          {getTierBadgeLabel(pkg)}
                        </span>
                        <div className="package-card-tactical-actions">
                          <button
                            type="button"
                            className="package-tactical-action-btn package-tactical-action-edit"
                            onClick={() => handleEditClick(pkg)}
                            title="Edit tier"
                          >
                            <i className="fas fa-pen" /> EDIT
                          </button>
                          <button
                            type="button"
                            className="package-tactical-action-btn package-tactical-action-delete"
                            onClick={() => handleDeletePackage(pkg.id)}
                            title="Delete tier"
                          >
                            <i className="fas fa-trash" /> DEL
                          </button>
                        </div>
                      </div>
                      <h3 className="package-card-tactical-name">{pkg.display_name}</h3>
                      <p className="package-card-tactical-desc">{pkg.description}</p>
                      <div className="package-card-tactical-prices">
                        <div className="package-card-tactical-price">
                          <span className="package-card-tactical-price-label">MONTHLY</span>
                          <span className="package-card-tactical-price-value opo-mono-value">${pkg.price_monthly}</span>
                          <span className="package-card-tactical-price-suffix">/ user / mo</span>
                        </div>
                        <div className="package-card-tactical-price">
                          <span className="package-card-tactical-price-label">YEARLY PREPAID</span>
                          <span className="package-card-tactical-price-value opo-mono-value">${pkg.price_yearly}</span>
                          <span className="package-card-tactical-price-suffix">/ user / yr</span>
                        </div>
                      </div>
                      {features.length > 0 && (
                        <div className="package-card-tactical-features">
                          <div className="package-card-tactical-features-label">INCLUDED</div>
                          <ul>
                            {features.map((feature, index) => (
                              <li key={index}>
                                <i className="fas fa-check" /> {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Strategic / non-SaaS revenue channels (display-only) */}
            <h3 className="opo-section-heading packages-section-heading packages-section-heading-secondary">
              <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
              STRATEGIC REVENUE CHANNELS
              <span className="opo-mono-value packages-section-count">{REVENUE_CHANNELS.length}</span>
            </h3>
            <p className="packages-section-sub">
              Non-SaaS income streams driving 2026–2027 priority. Edit upstream in the business-model doc;
              this view reflects the canonical record.
            </p>

            <div className="packages-channels-grid">
              {REVENUE_CHANNELS.map((ch) => (
                <div
                  key={ch.key}
                  className={`opo-card-tactical opo-card-${ch.level} packages-channel-card`}
                >
                  <div className="packages-channel-head">
                    <span className={`packages-channel-icon packages-channel-icon-${ch.level}`}>
                      <i className={`fas ${ch.icon}`} />
                    </span>
                    <span className={`opo-status-tag opo-tag-${ch.level}`}>{ch.tag}</span>
                  </div>
                  <h4 className="packages-channel-title">{ch.title}</h4>
                  <div className="packages-channel-pricing opo-mono-value">{ch.pricingLine}</div>
                  <p className="packages-channel-summary">{ch.summary}</p>
                  <ul className="packages-channel-bullets">
                    {ch.bullets.map((b, i) => (
                      <li key={i}><i className="fas fa-angle-right" /> {b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="packages-footnote">
              <i className="fas fa-info-circle" />
              Pilot / reference-implementation work (Optimum Realty Inc.) is a strategic
              partnership — paid-in-kind through reference rights, office co-location, and training rights —
              and intentionally not modelled as a priced channel here.
            </div>
          </div>
        </div>
      </div>

      {/* Package Modal */}
      <PackageModal
        package={selectedPackage}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSavePackage}
        isNewPackage={!selectedPackage}
      />
    </div>
  );
}

function formatBusinessType(value) {
  if (!value) return 'Not specified';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

function companyMasterAdminName(company) {
  if (company.admin_first_name && company.admin_last_name) {
    return `${company.admin_first_name} ${company.admin_last_name}`;
  }
  return null;
}

function CompanyDlRow({ label, children }) {
  return (
    <div className="company-dl-row">
      <dt>{label}</dt>
      <dd>{children || '—'}</dd>
    </div>
  );
}

function CompanyCard({
  company,
  buildings = [],
  onEdit,
  onAddBuilding,
  onEditBuilding,
  defaultExpanded = false,
  highlightBuildingId = null,
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (defaultExpanded) setIsExpanded(true);
  }, [defaultExpanded]);

  const extraUsers = (company.company_users || []).filter(
    (user) => user.id !== company.master_admin_user_id
  );
  const masterName = companyMasterAdminName(company);
  const hqAddress = buildRetailCompanyAddressString(company);
  const buildingCount = buildings.length;

  return (
    <div className={`company-card${isExpanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="company-card-header"
        onClick={() => setIsExpanded((open) => !open)}
        aria-expanded={isExpanded}
      >
        <div className="company-main-info">
          <div className="company-name-section">
            <h3>{company.company_name}</h3>
            <div className="company-badges">
              <span className={`package-badge ${(company.package_tier || 'bronze').toLowerCase()}`}>
                {company.package_tier || 'Bronze'}
              </span>
              {company.master_admin_created && (
                <span className="status-badge admin-created">
                  <i className="fas fa-user-shield"></i> Admin created
                </span>
              )}
              {company.contract_signed ? (
                <span className="status-badge contract-signed">Contract signed</span>
              ) : null}
            </div>
          </div>
          <div className="company-summary company-summary-row">
            <div className="summary-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>{company.city ? `${company.city}${company.province ? `, ${company.province}` : ''}` : 'HQ not set'}</span>
            </div>
            <div className="summary-item">
              <i className="fas fa-user-tie"></i>
              <span>{masterName ? masterName : 'Master admin not set'}</span>
            </div>
            <div className="summary-item">
              <i className="fas fa-city"></i>
              <span>{buildingCount === 1 ? '1 building' : `${buildingCount} buildings`}</span>
            </div>
          </div>
        </div>
        <div className="expand-icon" aria-hidden="true">
          <i className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
        </div>
      </button>

      {isExpanded && (
        <div className="company-card-details">
          <div className="company-card-split">
            <section className="company-block">
              <h4>Identity</h4>
              <dl className="company-dl">
                <CompanyDlRow label="Legal name">{company.legal_name}</CompanyDlRow>
                <CompanyDlRow label="Business type">{formatBusinessType(company.business_type)}</CompanyDlRow>
                <CompanyDlRow label="Industry">{company.industry_sector}</CompanyDlRow>
                <CompanyDlRow label="Package">{company.package_tier || 'Bronze'}</CompanyDlRow>
              </dl>
            </section>
            <section className="company-block">
              <h4>Headquarters</h4>
              <dl className="company-dl">
                <CompanyDlRow label="Address">{hqAddress || 'Not on file'}</CompanyDlRow>
                <CompanyDlRow label="Email">
                  {company.email ? <a href={`mailto:${company.email}`}>{company.email}</a> : null}
                </CompanyDlRow>
                <CompanyDlRow label="Phone">{company.phone_number}</CompanyDlRow>
                <CompanyDlRow label="Website">
                  {company.website ? (
                    <a href={company.website} target="_blank" rel="noopener noreferrer">
                      {company.website} <i className="fas fa-external-link-alt"></i>
                    </a>
                  ) : null}
                </CompanyDlRow>
              </dl>
            </section>
          </div>

          <section className="company-block">
            <h4>Products</h4>
            <div className="products-list">
              {company.optiprime_core && <span className="product-badge core">OptiPrime Core</span>}
              {company.optilaw_legal && <span className="product-badge legal">OptiLaw Legal</span>}
              {!company.optiprime_core && !company.optilaw_legal && (
                <span className="no-products">No products selected</span>
              )}
            </div>
          </section>

          <section className="company-block">
            <h4>People</h4>
            <div className="users-list">
              {company.master_admin_created ? (
                <div className="user-item master-admin">
                  <i className="fas fa-user-shield"></i>
                  <span>{masterName || 'Master Admin'}</span>
                  <span className="user-role">Master Admin</span>
                </div>
              ) : (
                <div className="no-users">Master admin not created yet</div>
              )}
              {extraUsers.map((user) => (
                <div key={user.id} className="user-item employee">
                  <i className="fas fa-user"></i>
                  <span>{user.firstName} {user.lastName}</span>
                  <span className="user-role">{user.role_label || user.permission_level || user.department || 'User'}</span>
                </div>
              ))}
              {company.master_admin_created && extraUsers.length === 0 && (
                <div className="add-users-note">
                  <i className="fas fa-info-circle"></i>
                  <span>Additional users are added by the master admin</span>
                </div>
              )}
            </div>
          </section>

          <section className="company-block company-buildings-block">
            <div className="company-block-head">
              <h4>Buildings <span className="company-count-chip">{buildingCount}</span></h4>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddBuilding(company);
                }}
              >
                <i className="fas fa-plus"></i> Add building
              </button>
            </div>
            {buildingCount === 0 ? (
              <p className="company-buildings-empty">
                No buildings linked yet. Add the properties this enterprise operates.
              </p>
            ) : (
              <ul className="company-building-list">
                {buildings.map((bldg) => {
                  const addr = buildBuildingAddressString(bldg);
                  const highlighted = highlightBuildingId != null && Number(highlightBuildingId) === Number(bldg.id);
                  return (
                    <li
                      key={bldg.id}
                      className={`company-building-item${highlighted ? ' is-highlighted' : ''}`}
                    >
                      <div className="company-building-main">
                        <strong>{bldg.name}</strong>
                        <div className="company-building-meta">
                          {bldg.property_type ? (
                            <span className="company-building-type">{bldg.property_type}</span>
                          ) : null}
                          {formatOccupancyCount(bldg) ? (
                            <span>{formatOccupancyCount(bldg)}</span>
                          ) : null}
                        </div>
                        {bldg.building_name ? (
                          <span className="company-building-wing">{bldg.building_name}</span>
                        ) : null}
                        <span className="company-building-addr">{addr || 'Address not on file'}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditBuilding(company, bldg);
                        }}
                      >
                        <i className="fas fa-edit"></i> Edit
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <div className="card-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(company);
              }}
            >
              <i className="fas fa-edit"></i> Edit company
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Chatham House — BETA GROUP 1 pilot location; matches buildings map / user data */
const RETAIL_MAP_CHATHAM_LAT = 49.159176;
const RETAIL_MAP_CHATHAM_LNG = -123.142862;
const CHATHAM_GEOCODE_QUERY = '7400 Moffatt Road, Richmond, BC, Canada';

/**
 * Nominatim often misses Canadian "suite - street" lines and "City BC" + full province duplicates.
 * Try several query strings (full DB line first, then simplified) until one returns a hit.
 */
function retailGeocodeQueryCandidates(company) {
  const full = buildRetailCompanyAddressString(company);
  const out = [];
  const seen = new Set();
  const push = (q) => {
    const s = (q || '').replace(/\s+/g, ' ').trim();
    if (s.length >= 6 && !seen.has(s.toLowerCase())) {
      seen.add(s.toLowerCase());
      out.push(s);
    }
  };

  push(full);

  const streetRaw = (company.street_address || '').trim();
  const cityRaw = (company.city || '').trim();
  const prov = (company.province || '').trim();
  const pc = (company.postal_code || '').trim();
  const country = (company.country || 'Canada').trim();

  if (streetRaw) {
    const withoutSuitePrefix = streetRaw.replace(/^\d+\s*-\s*/, '').trim();
    if (withoutSuitePrefix !== streetRaw) {
      push([withoutSuitePrefix, cityRaw, prov, pc, country].filter(Boolean).join(', '));
    }
    let city = cityRaw.replace(/\s+BC\s*$/i, '').replace(/\s+British Columbia\s*$/i, '').trim();
    if (!city && cityRaw) city = cityRaw.replace(/\s*,\s*/g, ' ').trim();
    const streetLine = (withoutSuitePrefix && withoutSuitePrefix !== streetRaw) ? withoutSuitePrefix : streetRaw;
    push([streetLine, city || cityRaw, prov || 'British Columbia', pc, country].filter(Boolean).join(', '));
  }

  if (pc.length >= 5) {
    const pcNorm = pc.replace(/\s+/g, ' ').trim();
    push(`${pcNorm}, ${country}`);
  }

  return out;
}

function retailCompanyIsBetaGroupChatham(company) {
  const n = (company.company_name || '').toLowerCase();
  return /\bbeta\b/.test(n) && /\bgroup\b/.test(n);
}

async function nominatimGeocodeRetailAddress(query) {
  if (!query || query.length < 4) return null;
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
  const response = await fetch(url, {
    headers: {
      'Accept-Language': 'en',
    },
  });
  if (!response.ok) return null;
  const data = await response.json();
  if (data && data[0]) {
    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
      source: 'geocoded',
    };
  }
  return null;
}

async function resolveRetailCompanyLatLng(company) {
  const rawLat = company.latitude != null ? String(company.latitude).trim() : '';
  const rawLng = company.longitude != null ? String(company.longitude).trim() : '';
  if (rawLat && rawLng) {
    const lat = parseFloat(rawLat.replace(',', '.'));
    const lng = parseFloat(rawLng.replace(',', '.'));
    if (!Number.isNaN(lat) && !Number.isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return { lat, lng, source: 'stored' };
    }
  }

  if (retailCompanyIsBetaGroupChatham(company)) {
    const geo = await nominatimGeocodeRetailAddress(CHATHAM_GEOCODE_QUERY);
    if (geo) return geo;
    return { lat: RETAIL_MAP_CHATHAM_LAT, lng: RETAIL_MAP_CHATHAM_LNG, source: 'chatham-fallback' };
  }

  const candidates = retailGeocodeQueryCandidates(company);
  if (candidates.length === 0) return null;
  for (let i = 0; i < candidates.length; i += 1) {
    const geo = await nominatimGeocodeRetailAddress(candidates[i]);
    if (geo) return geo;
    if (i < candidates.length - 1) {
      await new Promise((r) => setTimeout(r, 400));
    }
  }
  return null;
}

function RetailCompaniesMapFitBounds({ companies, coordsById }) {
  const map = useMap();
  useEffect(() => {
    const pts = companies
      .map((c) => coordsById[c.id])
      .filter(Boolean)
      .map((p) => [p.lat, p.lng])
      .filter(([lat, lng]) => !Number.isNaN(lat) && !Number.isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180);
    if (pts.length === 0) {
      map.setView([20, 0], 2);
      return;
    }
    if (pts.length === 1) {
      map.setView(pts[0], 12);
      return;
    }
    map.fitBounds(L.latLngBounds(pts), { padding: [48, 48], maxZoom: 14 });
  }, [map, companies, coordsById]);
  return null;
}

function RetailUsersPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewCompany, setIsNewCompany] = useState(false);

  const [filterCountry, setFilterCountry] = useState('');
  const [filterCompanyName, setFilterCompanyName] = useState('');
  const [filterPackageTier, setFilterPackageTier] = useState('');
  const [countryOptions, setCountryOptions] = useState([]);
  const [companyNameOptions, setCompanyNameOptions] = useState([]);
  const [packageTierOptions, setPackageTierOptions] = useState([]);
  const [allBuildings, setAllBuildings] = useState([]);
  const [focusCompanyId, setFocusCompanyId] = useState(null);
  const [focusBuildingId, setFocusBuildingId] = useState(null);
  const [buildingModalOpen, setBuildingModalOpen] = useState(false);
  const [buildingModalCompany, setBuildingModalCompany] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [isNewBuilding, setIsNewBuilding] = useState(false);

  // Theme state
  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme ? 'dark' : 'light');
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const fetchCompanies = () => {
    setLoading(true);
    setError('');
    Promise.all([
      fetch(`${API_URL}/api/retail-companies`).then((res) => {
        if (!res.ok) throw new Error('Failed to fetch companies');
        return res.json();
      }),
      fetch(`${API_URL}/api/buildings`).then((res) => (res.ok ? res.json() : [])),
    ])
      .then(([companies, buildings]) => {
        setAllCompanies(companies);
        setAllBuildings(Array.isArray(buildings) ? buildings : []);
        const countries = [...new Set(companies.map((c) => c.country).filter(Boolean))].sort();
        const names = [...new Set(companies.map((c) => c.company_name).filter(Boolean))].sort();
        const tiers = [...new Set(companies.map((c) => c.package_tier).filter(Boolean))].sort();
        setCountryOptions(countries);
        setCompanyNameOptions(names);
        setPackageTierOptions(tiers);
        setError('');
      })
      .catch(() => {
        setError('Could not load enterprise companies.');
        setAllCompanies([]);
        setAllBuildings([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const buildingsForCompany = (companyId) =>
    allBuildings.filter((b) => Number(b.company_id) === Number(companyId));

  const filteredCompanies = useMemo(() => {
    return allCompanies.filter((c) => {
      if (filterCountry && (c.country || '') !== filterCountry) return false;
      if (filterCompanyName && c.company_name !== filterCompanyName) return false;
      if (filterPackageTier && (c.package_tier || '') !== filterPackageTier) return false;
      return true;
    });
  }, [allCompanies, filterCountry, filterCompanyName, filterPackageTier]);

  const filteredCompanyIds = useMemo(
    () => new Set(filteredCompanies.map((c) => Number(c.id))),
    [filteredCompanies]
  );

  const mapBuildings = useMemo(() => {
    return allBuildings.filter((b) => {
      if (b.company_id != null && !filteredCompanyIds.has(Number(b.company_id))) return false;
      if (b.latitude == null || b.longitude == null) return false;
      if (String(b.latitude).trim() === '' || String(b.longitude).trim() === '') return false;
      const lat = parseFloat(String(b.latitude).replace(',', '.'));
      const lng = parseFloat(String(b.longitude).replace(',', '.'));
      return !Number.isNaN(lat) && !Number.isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
    });
  }, [allBuildings, filteredCompanyIds]);

  function handleAddClick() {
    setSelectedCompany(null);
    setIsNewCompany(true);
    setIsModalOpen(true);
  }

  function handleEditClick(company) {
    setSelectedCompany(company);
    setIsNewCompany(false);
    setIsModalOpen(true);
  }

  function handleSaveCompany() {
    fetchCompanies();
    setIsModalOpen(false);
  }

  function handleAddBuilding(company) {
    setBuildingModalCompany(company);
    setSelectedBuilding(null);
    setIsNewBuilding(true);
    setBuildingModalOpen(true);
    setFocusCompanyId(company.id);
  }

  function handleEditBuilding(company, building) {
    setBuildingModalCompany(company);
    setSelectedBuilding(building);
    setIsNewBuilding(false);
    setBuildingModalOpen(true);
    setFocusCompanyId(company.id);
    setFocusBuildingId(building.id);
  }

  function handleSaveBuilding() {
    setBuildingModalOpen(false);
    setSelectedBuilding(null);
    fetchCompanies();
  }

  function handleMapBuildingClick(building) {
    const company = allCompanies.find((c) => Number(c.id) === Number(building.company_id));
    setFocusCompanyId(building.company_id);
    setFocusBuildingId(building.id);
    if (company) {
      setTimeout(() => {
        const el = document.getElementById(`enterprise-card-${company.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1><i className="fas fa-building"></i> Enterprise Users</h1>
            </div>
            <div className="top-nav-actions">
              <button 
                className="icon-button settings-button" 
                onClick={() => {
                  const settingsEvent = new CustomEvent('openAdminSettings');
                  window.dispatchEvent(settingsEvent);
                }}
                title="Admin Settings"
              >
                <i className="fas fa-cog"></i>
              </button>
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button 
                className="icon-button logout-button" 
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">
          <div className="card users-card">
            <div className="card-header">
              <div className="header-title-section">
                <h2>Enterprise Companies Overview</h2>
                {/* Dynamic Data Pills */}
                <div className={`user-stats-pills ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
                  {(() => {
                    const totalCompanies = allCompanies.length;
                    const activeCompanies = allCompanies.filter(c => c.is_active).length;
                    const companiesWithContracts = allCompanies.filter(c => c.contract_signed).length;
                    const linkedBuildings = allBuildings.filter((b) => b.company_id != null).length;

                    return (
                      <>
                        <div className="stat-pill total">
                          <span className="pill-icon">🏢</span>
                          <span className="pill-value">{totalCompanies}</span>
                          <span className="pill-label">Enterprises</span>
                        </div>

                        <div className="stat-pill online">
                          <span className="pill-icon">✅</span>
                          <span className="pill-value">{activeCompanies}</span>
                          <span className="pill-label">Active</span>
                        </div>

                        <div className="stat-pill enabled">
                          <span className="pill-icon">🏙️</span>
                          <span className="pill-value">{linkedBuildings}</span>
                          <span className="pill-label">Buildings</span>
                        </div>

                        <div className="stat-pill offline">
                          <span className="pill-icon">📄</span>
                          <span className="pill-value">{companiesWithContracts}</span>
                          <span className="pill-label">Contracts</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="card-actions">
                <button className="btn btn-primary" onClick={handleAddClick}>
                  <i className="fas fa-plus"></i> Add New Enterprise
                </button>
              </div>
            </div>

            {!loading && !error && allCompanies.length > 0 && (
              <>
                <div className="enterprise-filters">
                  <div>
                    <label>Country</label>
                    <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}>
                      <option value="">All</option>
                      {countryOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Company</label>
                    <select value={filterCompanyName} onChange={(e) => setFilterCompanyName(e.target.value)}>
                      <option value="">All</option>
                      {companyNameOptions.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Package</label>
                    <select value={filterPackageTier} onChange={(e) => setFilterPackageTier(e.target.value)}>
                      <option value="">All</option>
                      {packageTierOptions.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setFilterCountry('');
                      setFilterCompanyName('');
                      setFilterPackageTier('');
                    }}
                  >
                    Clear
                  </button>
                </div>
                <div className="card-content buildings-page-map-content enterprise-map-wrap">
                  <div className="buildings-page-map-container">
                    <p className="buildings-page-map-subtitle">
                      {mapBuildings.length} {mapBuildings.length === 1 ? 'building' : 'buildings'} on the map
                      {allBuildings.length > mapBuildings.length
                        ? ` · ${allBuildings.length - mapBuildings.length} without coordinates or outside the current filter`
                        : ''}
                    </p>
                    <div className="buildings-page-map-frame retail-users-map-frame">
                      <MapContainer center={[20, 0]} zoom={2} scrollWheelZoom className="buildings-page-map-leaflet">
                        <TileLayer
                          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          crossOrigin
                        />
                        <BuildingsPageMapFitBounds buildings={mapBuildings} />
                        {mapBuildings.map((building) => {
                          const lat = parseFloat(String(building.latitude).replace(',', '.'));
                          const lng = parseFloat(String(building.longitude).replace(',', '.'));
                          return (
                            <Marker
                              key={building.id}
                              position={[lat, lng]}
                              eventHandlers={{
                                click: () => handleMapBuildingClick(building),
                              }}
                            />
                          );
                        })}
                      </MapContainer>
                    </div>
                    <p className="buildings-page-map-footnote">
                      Pins are buildings belonging to an enterprise. Click a pin to open that company and highlight the building.
                    </p>
                  </div>
                  {mapBuildings.length === 0 && filteredCompanies.length > 0 && (
                    <div className="buildings-page-map-empty" style={{ marginTop: 12 }}>
                      <p>No buildings with coordinates in the current view.</p>
                      <p className="buildings-page-map-empty-hint">
                        Expand an enterprise and add a building, or use Find Coordinates from Address in the building form.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Companies grid */}
            <div className="data-table-container">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading enterprise companies...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <i className="fas fa-exclamation-triangle"></i>
                <p>{error}</p>
                <button className="btn btn-secondary" onClick={fetchCompanies}>
                  <i className="fas fa-redo"></i> Retry
                </button>
              </div>
            ) : allCompanies.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-building"></i>
                <h3>No Enterprise Companies Yet</h3>
                <p>Start by adding your first enterprise client to the system.</p>
                <button className="btn btn-primary" onClick={handleAddClick}>
                  <i className="fas fa-plus"></i> Add First Company
                </button>
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-filter"></i>
                <h3>No companies match filters</h3>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ marginTop: 12 }}
                  onClick={() => {
                    setFilterCountry('');
                    setFilterCompanyName('');
                    setFilterPackageTier('');
                  }}
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="companies-grid">
                {filteredCompanies.map((company) => (
                  <div key={company.id} id={`enterprise-card-${company.id}`}>
                    <CompanyCard
                      company={company}
                      buildings={buildingsForCompany(company.id)}
                      onEdit={handleEditClick}
                      onAddBuilding={handleAddBuilding}
                      onEditBuilding={handleEditBuilding}
                      defaultExpanded={
                        Number(focusCompanyId) === Number(company.id) ||
                        buildingsForCompany(company.id).length > 0
                      }
                      highlightBuildingId={
                        Number(focusCompanyId) === Number(company.id) ? focusBuildingId : null
                      }
                    />
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      <RetailCompanyModal
        company={selectedCompany}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveCompany}
        isNewCompany={isNewCompany}
      />

      <BuildingModal
        building={selectedBuilding}
        isOpen={buildingModalOpen}
        onClose={() => setBuildingModalOpen(false)}
        onSave={handleSaveBuilding}
        isNewBuilding={isNewBuilding}
        companyId={buildingModalCompany ? buildingModalCompany.id : null}
        companyName={buildingModalCompany ? buildingModalCompany.company_name : ''}
      />
      </div>
    </ThemeContext.Provider>
  );
}

/** Fit map to building markers on /buildings (same Leaflet/OSM stack as BuildingModal). */
function BuildingsPageMapFitBounds({ buildings }) {
  const map = useMap();
  useEffect(() => {
    const pts = buildings
      .map((b) => {
        const lat = parseFloat(String(b.latitude).replace(',', '.'));
        const lng = parseFloat(String(b.longitude).replace(',', '.'));
        if (Number.isNaN(lat) || Number.isNaN(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
        return [lat, lng];
      })
      .filter(Boolean);
    if (pts.length === 0) {
      map.setView([20, 0], 2);
      return;
    }
    if (pts.length === 1) {
      map.setView(pts[0], 14);
      return;
    }
    map.fitBounds(L.latLngBounds(pts), { padding: [48, 48], maxZoom: 14 });
  }, [map, buildings]);
  return null;
}

function BuildingsPage() {
  return <Navigate to="/retail-users" replace />;
}

/** Count PostgreSQL vs SQLite entries under one platform in GET /api/admin/database-inventory */
function countDbEnginesByPlatform(inv, platformId) {
  if (!inv || !Array.isArray(inv.platforms)) return { postgres: 0, sqlite: 0 };
  const plat = inv.platforms.find((p) => p.id === platformId);
  if (!plat || !Array.isArray(plat.items)) return { postgres: 0, sqlite: 0 };
  return plat.items.reduce(
    (acc, item) => {
      if (item.engine === 'postgresql') acc.postgres += 1;
      else if (item.engine === 'sqlite') acc.sqlite += 1;
      return acc;
    },
    { postgres: 0, sqlite: 0 }
  );
}

/** Map an inventory status to the OPO tactical level (good/warning/critical/unknown). */
function dbStatusLevel(status) {
  switch (status) {
    case 'active':  return 'good';
    case 'archive': return 'warning';
    case 'error':   return 'critical';
    case 'empty':
    case 'missing': return 'unknown';
    default:        return 'unknown';
  }
}

const DB_STATUS_LABEL = {
  active: 'ACTIVE',
  empty: 'EMPTY',
  archive: 'ARCHIVE',
  error: 'ERROR',
  missing: 'MISSING',
};

function fmtBytesShort(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
}

/** PostgreSQL + SQLite inventory — data from GET /api/admin/database-inventory */
function DatabasesInventoryPanel({ showIntro = true }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [tickRefresh, setTickRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/admin/database-inventory`, { headers: adminSessionHeaders() })
      .then((r) => {
        if (r.status === 401) throw new Error('Session expired — log in again.');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        if (!cancelled) {
          setData(j);
          setErr('');
        }
      })
      .catch((e) => {
        if (!cancelled) setErr(e.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tickRefresh]);

  /* ── Derived metrics for hero/KPI ───────────────────────────────────── */
  const allRows = useMemo(() => {
    const rows = [];
    if (data && Array.isArray(data.platforms)) {
      for (const plat of data.platforms) {
        for (const item of (plat.items || [])) {
          rows.push({ ...item, _platform: plat.label });
        }
      }
    }
    return rows;
  }, [data]);

  const totals = useMemo(() => {
    const counts = { total: allRows.length, postgres: 0, sqlite: 0, sizeBytes: 0, tableCount: 0 };
    const byStatus = { active: 0, empty: 0, archive: 0, error: 0, missing: 0 };
    for (const r of allRows) {
      counts.sizeBytes += Number(r.sizeBytes || 0);
      counts.tableCount += Number(r.tableCount || 0);
      if (r.engine === 'postgresql') counts.postgres += 1;
      else if (r.engine === 'sqlite') counts.sqlite += 1;
      if (byStatus[r.status] != null) byStatus[r.status] += 1;
    }
    const errorRows = allRows.filter((r) => r.status === 'error').length;
    const archiveRows = allRows.filter((r) => r.status === 'archive').length;
    let level = 'good';
    let label = 'INVENTORY HEALTHY';
    if (errorRows > 0) { level = 'critical'; label = 'INVENTORY DEGRADED'; }
    else if (archiveRows > 0) { level = 'warning'; label = 'CLEANUP RECOMMENDED'; }
    return { ...counts, byStatus, level, label, errorRows, archiveRows };
  }, [allRows]);

  const maxRowSize = useMemo(() => allRows.reduce((m, r) => Math.max(m, Number(r.sizeBytes || 0)), 0) || 1, [allRows]);

  /* ── Loading / error ────────────────────────────────────────────────── */
  if (loading && !data) {
    return (
      <div className="opo-loading-container">
        <div className="opo-loading-spinner" />
        <p>Scanning PostgreSQL cluster + SQLite tree…</p>
      </div>
    );
  }
  if (err && !data) {
    return <div className="opo-error">Inventory uplink lost: {err}</div>;
  }

  /* ── Render ────────────────────────────────────────────────────────── */
  const populatedPlatforms = (data?.platforms || []).filter((p) => p.items && p.items.length > 0);
  const emptyPlatformLabels = (data?.platforms || [])
    .filter((p) => !p.items || p.items.length === 0)
    .map((p) => p.label);

  return (
    <div id="databases" className="opo-overview opo-tactical databases-tactical">
      {/* ─── Hero status banner ─────────────────────────────────────── */}
      <div className={`opo-hero opo-hero-${totals.level}`}>
        <div className="opo-hero-scan" aria-hidden="true" />
        <div className="opo-hero-left">
          <div className="opo-hero-eyebrow">DATABASE INVENTORY</div>
          <div className="opo-hero-title">
            <span className={`opo-hero-pulse opo-pulse-${totals.level}`} />
            <h2>{totals.label}</h2>
          </div>
          <div className="opo-hero-sub">
            {totals.total} databases ·
            {' '}{totals.postgres} PostgreSQL ·
            {' '}{totals.sqlite} SQLite ·
            {' '}{fmtBytesShort(totals.sizeBytes)} on disk
          </div>
        </div>
        <div className="opo-hero-right">
          <button
            type="button"
            className="opo-refresh-btn"
            onClick={() => setTickRefresh((n) => n + 1)}
            title="Re-scan inventory"
          >
            <span className="opo-refresh-icon" /> RESCAN
          </button>
          <div className="opo-hero-timestamp">
            {data?.generatedAt ? new Date(data.generatedAt).toLocaleString() : '—'}
          </div>
        </div>
      </div>

      {showIntro && (
        <p className="dashboard-section-subtitle databases-intro">
          Full scan of this host's PostgreSQL cluster (excluding <code>postgres</code>) and every
          {' '}<code>*.db</code> under <code>projects/nodes</code>. Symlinks are merged.
          Plan cleanup starting with <strong>Legacy / backups</strong>.
        </p>
      )}

      {/* ─── KPI strip ──────────────────────────────────────────────── */}
      <div className="opo-kpi-row">
        <div className="opo-kpi">
          <div className="opo-kpi-label">TOTAL DATABASES</div>
          <div className="opo-kpi-value opo-mono-value">{totals.total}</div>
          <div className="opo-kpi-foot">
            <span className="opo-level-good">●</span> {totals.byStatus.active} active
            {totals.byStatus.archive > 0 && <> · <span className="opo-level-warning">●</span> {totals.byStatus.archive} archive</>}
            {totals.byStatus.error > 0 && <> · <span className="opo-level-critical">●</span> {totals.byStatus.error} error</>}
          </div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">DISK FOOTPRINT</div>
          <div className="opo-kpi-value opo-mono-value">{fmtBytesShort(totals.sizeBytes)}</div>
          <div className="opo-kpi-foot">aggregate size of every catalogued DB</div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">PG / SQLITE SPLIT</div>
          <div className="opo-kpi-value opo-mono-value">
            {totals.postgres}<span className="opo-kpi-sep">/</span>{totals.sqlite}
          </div>
          <div className="opo-bar-track opo-bar-mini">
            <div
              className="opo-bar-fill opo-fill-good"
              style={{ width: `${totals.total ? (totals.postgres / totals.total) * 100 : 0}%` }}
            />
          </div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">TABLES INDEXED</div>
          <div className="opo-kpi-value opo-mono-value">{totals.tableCount.toLocaleString()}</div>
          <div className="opo-kpi-foot">
            {data?.summary?.sqliteFilesOnDisk ?? 0} SQLite paths · {data?.summary?.sqliteLogicalGroups ?? 0} unique files
          </div>
        </div>
      </div>

      {/* ─── Platform telemetry cards ───────────────────────────────── */}
      <div className="opo-section">
        <div className="opo-section-header">
          <h2>Platforms</h2>
          <div className="opo-section-meta">
            <span className="opo-section-counter opo-level-good">
              {populatedPlatforms.length}/{(data?.platforms || []).length} POPULATED
            </span>
          </div>
        </div>

        {populatedPlatforms.map((plat) => {
          const pgCount = plat.items.filter((i) => i.engine === 'postgresql').length;
          const sqCount = plat.items.filter((i) => i.engine === 'sqlite').length;
          const platBytes = plat.items.reduce((s, i) => s + Number(i.sizeBytes || 0), 0);
          const platTables = plat.items.reduce((s, i) => s + Number(i.tableCount || 0), 0);
          const errCount = plat.items.filter((i) => i.status === 'error').length;
          const archCount = plat.items.filter((i) => i.status === 'archive').length;
          const platLevel = errCount > 0 ? 'critical' : (archCount > 0 ? 'warning' : 'good');
          const platShare = totals.sizeBytes ? (platBytes / totals.sizeBytes) * 100 : 0;
          return (
            <div key={plat.id} className={`opo-stat-card opo-card-tactical opo-card-${platLevel} databases-platform-card`}>
              <div className="opo-stat-header">
                <span className="opo-service-name">
                  <span className={`opo-pip opo-pip-${platLevel}`} title={platLevel}>
                    <span className="opo-pip-core" />
                  </span>
                  {plat.label}
                </span>
                <span className={`opo-status-tag opo-tag-${platLevel}`}>{plat.items.length} DBs</span>
              </div>

              <p className="databases-platform-blurb">{plat.blurb}</p>

              <div className="databases-platform-metrics">
                <MetricBarInline label={`PostgreSQL · ${pgCount}`} value={pgCount} max={Math.max(pgCount + sqCount, 1)} level="good" suffix="" />
                <MetricBarInline label={`SQLite · ${sqCount}`} value={sqCount} max={Math.max(pgCount + sqCount, 1)} level="warning" suffix="" />
                <MetricBarInline label={`Disk share`} value={platShare} max={100} level={platLevel} suffix="%" />
              </div>
              <div className="databases-platform-foot">
                <span><strong>{fmtBytesShort(platBytes)}</strong> on disk</span>
                <span>·</span>
                <span><strong>{platTables.toLocaleString()}</strong> tables</span>
                {errCount > 0 && <><span>·</span><span className="opo-level-critical">{errCount} error</span></>}
                {archCount > 0 && <><span>·</span><span className="opo-level-warning">{archCount} archive</span></>}
              </div>

              <div className="databases-table-wrap">
                <table className="databases-table">
                  <thead>
                    <tr>
                      <th>STATE</th>
                      <th>ENGINE</th>
                      <th>NAME / PATH</th>
                      <th>SIZE</th>
                      <th>TABLES</th>
                      <th>STATUS</th>
                      <th>PURPOSE</th>
                      <th>LINKED</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plat.items.map((row) => {
                      const key = row.engine === 'postgresql' ? `pg:${row.name}` : row.id;
                      const lvl = dbStatusLevel(row.status);
                      const sizeBytes = Number(row.sizeBytes || 0);
                      const sizePct = (sizeBytes / maxRowSize) * 100;
                      const preview = (row.tablesPreview && row.tablesPreview.length)
                        ? row.tablesPreview.join(', ')
                        : '';
                      return (
                        <tr key={key} className={`databases-row databases-row-${lvl}`}>
                          <td className="databases-state-cell">
                            <span className={`opo-pip opo-pip-${lvl}`}><span className="opo-pip-core" /></span>
                          </td>
                          <td className="databases-engine-cell">
                            <span className={`databases-engine-badge databases-engine-${row.engine}`}>
                              {row.engine === 'postgresql' ? 'PG' : 'SQLITE'}
                            </span>
                          </td>
                          <td className="databases-name-cell">
                            <div className="opo-mono-value databases-name-text">
                              {row.engine === 'postgresql' ? row.name : row.relativePath}
                            </div>
                            {row.pathAliases && row.pathAliases.length > 0 && (
                              <div className="databases-aliases">
                                {row.pathAliases.map((a) => (
                                  <code key={a}>{a}</code>
                                ))}
                              </div>
                            )}
                            {preview && (
                              <div className="databases-preview" title={preview}>
                                {preview.length > 110 ? `${preview.slice(0, 110)}…` : preview}
                              </div>
                            )}
                          </td>
                          <td className="databases-size-cell">
                            <div className="databases-size-text opo-mono-value">{row.sizeLabel}</div>
                            <div className="opo-bar-track opo-bar-mini">
                              <div className={`opo-bar-fill opo-fill-${lvl === 'unknown' ? 'good' : lvl}`} style={{ width: `${Math.max(2, sizePct)}%` }} />
                            </div>
                          </td>
                          <td className="opo-mono-value databases-tables-cell">
                            {row.engine === 'postgresql' && row.tableError
                              ? <span className="opo-level-critical" title={row.tableError}>ERR</span>
                              : (row.tableCount == null ? '—' : row.tableCount.toLocaleString())}
                          </td>
                          <td>
                            <span className={`opo-status-tag opo-tag-${lvl}`}>{DB_STATUS_LABEL[row.status] || String(row.status || '—').toUpperCase()}</span>
                          </td>
                          <td className="databases-purpose">{row.purpose}</td>
                          <td className="databases-linked">{row.linkedTo}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {emptyPlatformLabels.length > 0 && (
          <div className="databases-empty-platforms">
            <span className="databases-empty-label">No databases catalogued for:</span>
            {emptyPlatformLabels.map((l) => (
              <span key={l} className="databases-empty-pill">{l}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Tiny inline metric bar — used in platform metric grids. */
function MetricBarInline({ label, value, max, level, suffix = '' }) {
  const pct = max ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="databases-mini-metric">
      <div className="databases-mini-metric-label">
        <span>{label}</span>
        <span className={`opo-mono-value opo-level-${level}`}>{value}{suffix}</span>
      </div>
      <div className="opo-bar-track opo-bar-mini">
        <div className={`opo-bar-fill opo-fill-${level}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function DatabasesPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') === 'dark');
  const toggleTheme = () => {
    const n = !isDark;
    setIsDark(n);
    document.documentElement.setAttribute('data-theme', n ? 'dark' : 'light');
    localStorage.setItem('theme', n ? 'dark' : 'light');
  };
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>🗄️ Databases</h1>
            </div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button
                type="button"
                className="icon-button logout-button"
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  localStorage.removeItem('sessionToken');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt" />
              </button>
            </div>
          </div>
          <div className="content-wrapper">
            <div className="dashboard-content" style={{ padding: '1rem 1.25rem' }}>
              <DatabasesInventoryPanel showIntro />
            </div>
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

// Admin Dashboard Component - Unified comprehensive overview
function DatabaseDashboard() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const [sessionExpired, setSessionExpired] = useState(false);

  // Core counts
  const [allUsers, setAllUsers] = useState([]);
  const [retailCompanies, setRetailCompanies] = useState([]);
  const [allBuildings, setAllBuildings] = useState([]);
  const [countriesCount, setCountriesCount] = useState(0);

  // Section summary data
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [wishListStats, setWishListStats] = useState(null);
  const [blogStats, setBlogStats] = useState(null);
  const [commsStats, setCommsStats] = useState(null);
  const [opoMetrics, setOpoMetrics] = useState(null);
  const [securityStats, setSecurityStats] = useState(null);
  const [systemStats, setSystemStats] = useState(null);
  const [backupStats, setBackupStats] = useState(null);
  const [usersByCountry, setUsersByCountry] = useState([]);
  const [servicePackages, setServicePackages] = useState([]);
  const [dbInventory, setDbInventory] = useState(null);
  const [dbInventoryLoaded, setDbInventoryLoaded] = useState(false);

  const [loading, setLoading] = useState(true);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [showSystemAlerts, setShowSystemAlerts] = useState(false);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme ? 'dark' : 'light');
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // System alerts — popup on dashboard load when active warnings exist
  useEffect(() => {
    if (sessionExpired || isSystemAlertsDismissed()) return;
    fetch(`${API_URL}/api/admin/system-alerts`, { headers: adminSessionHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const alerts = data?.alerts || [];
        if (alerts.length > 0) {
          setSystemAlerts(alerts);
          setShowSystemAlerts(true);
        }
      })
      .catch(() => {});
  }, [sessionExpired]);

  // Session check (admin card removed; still detect expired session on load)
  useEffect(() => {
    fetch(`${API_URL}/api/admin/info`, { headers: adminSessionHeaders() })
      .then(res => {
        if (res.status === 401) {
          setSessionExpired(true);
          throw new Error('Unauthorized');
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Database inventory — powers OptiPrime / OptiLaw summary pills (full detail on /databases)
  useEffect(() => {
    fetch(`${API_URL}/api/admin/database-inventory`, { headers: adminSessionHeaders() })
      .then((res) => {
        if (res.status === 401) {
          setSessionExpired(true);
          return null;
        }
        return res.ok ? res.json() : null;
      })
      .then((data) => setDbInventory(data))
      .catch(() => setDbInventory(null))
      .finally(() => setDbInventoryLoaded(true));
  }, []);

  // Fetch all dashboard data in parallel
  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      // PostgreSQL BETA users (same source as /users page) — NOT legacy SQLite /api/search
      fetch(`${API_URL}/api/community/users`, { headers: { ...adminSessionHeaders() } }).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/retail-companies`).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/buildings`).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/users/countries/count`).then(r => r.ok ? r.json() : { count: 0 }),
      fetch(`${MAIN_BACKEND_URL}/api/feedback/admin/summary`).then(r => r.ok ? r.json() : null),
      fetch(`${MAIN_BACKEND_URL}/api/widget-requests/admin/stats`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/blog-manager/blogs`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/communications/stats`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/opticonnector/metrics`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/security/latest-report`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/system/stats`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/backups/inventory`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/service-packages`).then(r => r.ok ? r.json() : []),
    ]).then(results => {
      const [
        usersRes, retailRes, buildingsRes, countriesRes,
        feedbackRes, widgetRes, blogRes, commsRes,
        opoRes, securityRes, sysRes, backupRes, packagesRes
      ] = results.map(r => r.status === 'fulfilled' ? r.value : null);

      const users = Array.isArray(usersRes) ? usersRes : [];
      const retail = Array.isArray(retailRes) ? retailRes : [];
      const buildings = Array.isArray(buildingsRes) ? buildingsRes : [];

      setAllUsers(users);
      setRetailCompanies(retail);
      setAllBuildings(buildings);
      setCountriesCount(countriesRes?.count || 0);

      // User distribution by country
      const countryMap = {};
      users.forEach(u => {
        const c = u.country || 'Unknown';
        countryMap[c] = (countryMap[c] || 0) + 1;
      });
      setUsersByCountry(Object.entries(countryMap).map(([country, count]) => ({ country, count })).sort((a,b) => b.count - a.count));

      // Feedback
      setFeedbackStats(feedbackRes?.summary?.overall || null);

      // Wish list / Widgets
      setWishListStats(widgetRes?.stats || null);

      // Blog
      if (blogRes?.blogs) {
        const blogs = blogRes.blogs;
        setBlogStats({
          total: blogs.length,
          published: blogs.filter(b => b.status === 'published').length,
          highlights: blogs.filter(b => b.highlight).length,
        });
      }

      // Communications
      setCommsStats(commsRes?.stats || null);

      // OPO metrics
      setOpoMetrics(opoRes || null);

      // Security
      setSecurityStats(securityRes?.scan || null);

      // System stats
      setSystemStats(sysRes || null);
      setBackupStats(backupRes?.summary ? backupRes : null);

      // Service packages (same /api/service-packages as Package Management tab → legacy 5000)
      setServicePackages(Array.isArray(packagesRes) ? packagesRes : []);
    }).finally(() => setLoading(false));
  }, []);

  const toggleMobileMenu = () => setIsMobileMenuOpen(m => !m);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Derived values (must match UsersPage presence semantics)
  const now = new Date();
  /** Ever logged in at least once (users.lastlogin set) — NOT currently online */
  const betaUsersWithLogin = allUsers.filter((u) => {
    if (u.lastLogin == null || String(u.lastLogin).trim() === '') return false;
    return !Number.isNaN(new Date(u.lastLogin).getTime());
  });
  /** Currently online — PG user_sessions last_activity within 5 min (same as /users) */
  const loggedInNowUsers = allUsers.filter((u) => u.isLoggedIn === true);
  const activeUsers30d = allUsers.filter(u => u.lastLogin && (now - new Date(u.lastLogin)) < 30 * 24 * 3600 * 1000).length;
  const onlineUsers24h = allUsers.filter(u => u.lastLogin && (now - new Date(u.lastLogin)) < 24 * 3600 * 1000).length;

  const fmtBytes = (bytes) => {
    if (!bytes) return '—';
    const gb = bytes / (1024 ** 3);
    return gb >= 1 ? `${gb.toFixed(1)} GB` : `${(bytes / (1024 ** 2)).toFixed(0)} MB`;
  };
  const fmtUptime = (seconds) => {
    if (!seconds) return '—';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    return d > 0 ? `${d}d ${h}h` : `${h}h`;
  };

  // Section card helper — tactical/console style; "Coming Soon" if no data
  const SectionCard = ({ icon, title, to, children, comingSoon, level = 'good' }) => (
    <div className={`opo-stat-card opo-card-tactical opo-card-${comingSoon ? 'unknown' : level} dashboard-section-card`}>
      <div className="opo-stat-header">
        <span className="opo-service-name">
          <span className="dashboard-card-icon" aria-hidden="true">{icon}</span> {title}
        </span>
        <Link to={to} className="dashboard-card-link">VIEW →</Link>
      </div>
      {comingSoon ? (
        <div className="dashboard-card-coming-soon">
          <span className="opo-status-tag opo-tag-warning">COMING SOON</span>
          <div className="dashboard-card-coming-sub">No data yet</div>
        </div>
      ) : (
        <div className="opo-stat-details">{children}</div>
      )}
    </div>
  );

  const StatRow = ({ label, value, highlight }) => (
    <div className="opo-detail-row">
      <span>{label}</span>
      <span className={`opo-mono-value ${highlight ? 'dashboard-stat-highlight' : ''}`}>{value ?? '—'}</span>
    </div>
  );

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar className={isMobileMenuOpen ? 'mobile-open' : ''} onMobileToggle={toggleMobileMenu} />
        <div className={`mobile-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}></div>
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>

          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <button className="mobile-menu-btn" onClick={toggleMobileMenu} title="Toggle Menu"
              style={{ display: window.innerWidth <= 768 ? 'flex' : 'none', marginRight: '12px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}>
              <div className="hamburger-icon">
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
              </div>
            </button>
            <div className="nav-title" style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: '1 1 auto', minWidth: 0 }}>
                <div style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.25 }}>
                  <span style={{ color: '#19EFFF' }}>Welcome to Opti</span><span style={{ color: 'var(--text-primary, #fff)' }}>Prime</span>
                </div>
                <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-secondary, rgba(255,255,255,0.78))', marginTop: '2px', lineHeight: 1.25 }}>
                  Dashboard of Dashboards
                </div>
              </div>
              <div className="top-nav-actions" style={{ flexShrink: 0 }}>
                <button className="icon-button settings-button" onClick={() => window.dispatchEvent(new CustomEvent('openAdminSettings'))} title="Admin Settings">
                  <i className="fas fa-cog"></i>
                </button>
                <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
                <button className="icon-button logout-button" onClick={() => { localStorage.removeItem('authToken'); localStorage.removeItem('userSession'); localStorage.removeItem('sessionToken'); window.location.href = '/'; }} title="Logout">
                  <i className="fas fa-sign-out-alt"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="content-wrapper content-wrapper--overview-dashboard">
            <div className="dashboard-content">

              {showSystemAlerts && systemAlerts.length > 0 && (
                <SystemAlertsModal
                  alerts={systemAlerts}
                  onClose={() => setShowSystemAlerts(false)}
                />
              )}

              {/* Session expired warning */}
              {sessionExpired && (
                <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '10px', padding: '14px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#ef4444', fontWeight: 600 }}>⚠️ Your session has expired.</span>
                  <button onClick={() => { localStorage.removeItem('sessionToken'); window.location.href = '/'; }}
                    style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 16px', cursor: 'pointer', fontWeight: 600 }}>
                    Log In Again
                  </button>
                </div>
              )}

              {/* ===================== DASHBOARD OF DASHBOARDS ===================== */}
              <div className="analytics-section analytics-section--overview-first opo-tactical">

                {/* ─── Platform status hero ─────────────────────────────────────── */}
                {(() => {
                  const cpuVal = systemStats?.cpu?.usage ?? 0;
                  const memUsed = systemStats?.memory?.used ?? 0;
                  const memTotal = systemStats?.memory?.total ?? 1;
                  const memPct = memTotal ? (memUsed / memTotal) * 100 : 0;
                  const critFindings = securityStats?.critical_count ?? 0;
                  const totalFindings = securityStats?.total_findings ?? 0;
                  let level = 'good';
                  let label = 'OPERATIONAL';
                  if (sessionExpired || critFindings > 0 || cpuVal >= 85 || memPct >= 90) {
                    level = 'critical'; label = 'CRITICAL';
                  } else if (totalFindings > 5 || cpuVal >= 60 || memPct >= 70) {
                    level = 'warning'; label = 'DEGRADED';
                  }
                  return (
                    <div className={`opo-hero opo-hero-${level}`}>
                      <div className="opo-hero-scan" aria-hidden="true" />
                      <div className="opo-hero-left">
                        <div className="opo-hero-eyebrow">PLATFORM STATUS</div>
                        <div className="opo-hero-title">
                          <span className={`opo-hero-pulse opo-pulse-${level}`} />
                          <h2>{label}</h2>
                        </div>
                        <div className="opo-hero-sub">
                          {loggedInNowUsers.length} logged in now · {betaUsersWithLogin.length} have logged in · {activeUsers30d} active 30d · {allBuildings.length} buildings · {countriesCount} countries
                        </div>
                      </div>
                      <div className="opo-hero-right">
                        <div className="opo-hero-timestamp">
                          {new Date().toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ─── KPI strip ────────────────────────────────────────────────── */}
                <div className="opo-kpi-row">
                  <div className="opo-kpi">
                    <div className="opo-kpi-label">LOGGED IN USERS</div>
                    <div className={`opo-kpi-value opo-mono-value ${loggedInNowUsers.length > 0 ? 'opo-level-good' : ''}`}>
                      {loggedInNowUsers.length.toLocaleString()}
                    </div>
                    <div className="opo-kpi-foot">
                      session activity ≤5 min · {allUsers.length ? ((loggedInNowUsers.length / allUsers.length) * 100).toFixed(0) : 0}% of accounts
                    </div>
                  </div>
                  <div className="opo-kpi">
                    <div className="opo-kpi-label">BETA USERS · EVER LOGGED IN</div>
                    <div className="opo-kpi-value opo-mono-value">{betaUsersWithLogin.length.toLocaleString()}</div>
                    <div className="opo-kpi-foot">total accounts: {allUsers.length.toLocaleString()}</div>
                  </div>
                  <div className="opo-kpi">
                    <div className="opo-kpi-label">ACTIVE · 30 DAYS</div>
                    <div className="opo-kpi-value opo-mono-value">{activeUsers30d.toLocaleString()}</div>
                    <div className="opo-bar-track opo-bar-mini">
                      <div
                        className={`opo-bar-fill opo-fill-good`}
                        style={{ width: `${allUsers.length ? Math.min(100, (activeUsers30d / allUsers.length) * 100) : 0}%` }}
                      />
                    </div>
                    <div className="opo-kpi-foot">last 24h login: {onlineUsers24h.toLocaleString()}</div>
                  </div>
                  <div className="opo-kpi">
                    <div className="opo-kpi-label">BUILDINGS</div>
                    <div className="opo-kpi-value opo-mono-value">{allBuildings.length.toLocaleString()}</div>
                    <div className="opo-kpi-foot">{countriesCount} countries · {retailCompanies.length} retail cos</div>
                  </div>
                </div>

                <h2 className="overview-section-heading dashboard-section-heading">Section Overview — All Dashboards</h2>
                <p className="dashboard-section-subtitle">
                  Quick snapshot of every section. Click "VIEW" to go to the full dashboard.
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '1rem',
                }}>

                  {/* BETA Users */}
                  <SectionCard icon="👥" title="BETA Users" to="/users">
                    <StatRow label="Logged in now" value={loggedInNowUsers.length} highlight />
                    <StatRow label="Ever logged in (≥1)" value={betaUsersWithLogin.length} />
                    <StatRow label="Total accounts (PostgreSQL)" value={allUsers.length} />
                    <StatRow label="Active (30 days)" value={activeUsers30d} />
                  </SectionCard>

                  {/* Enterprise Users */}
                  <SectionCard icon="🏢" title="Enterprise Users" to="/retail-users">
                    <StatRow label="Companies" value={retailCompanies.length} highlight />
                    <StatRow label="Buildings" value={allBuildings.length} />
                    <StatRow label="With master admin" value={retailCompanies.filter(c => c.master_admin_created).length} />
                    <StatRow label="OptiPrime Core" value={retailCompanies.filter(c => c.optiprime_core).length} />
                  </SectionCard>

                  {/* Package Management */}
                  <SectionCard icon="📦" title="Package Management" to="/packages"
                    comingSoon={servicePackages.length === 0}>
                    {servicePackages.length > 0 && <>
                      <StatRow label="Total packages" value={servicePackages.length} highlight />
                      <StatRow label="Active" value={servicePackages.filter((p) => p.is_active === 1 || p.is_active === true).length} />
                      <StatRow label="Tiers" value={[...new Set(servicePackages.map((p) => p.package_tier).filter(Boolean))].length} />
                      <StatRow label="Lowest monthly" value={servicePackages.length ? `$${Math.min(...servicePackages.map((p) => Number(p.price_monthly) || 0))}` : '—'} />
                    </>}
                  </SectionCard>

                  {/* Feedback */}
                  <SectionCard icon="💬" title="Feedback" to="/feedback"
                    comingSoon={!feedbackStats}>
                    {feedbackStats && <>
                      <StatRow label="Total submissions" value={feedbackStats.total_feedback} highlight />
                      <StatRow label="Unique contributors" value={feedbackStats.unique_users} />
                      <StatRow label="Approved" value={feedbackStats.total_approved} />
                      <StatRow label="Pending review" value={feedbackStats.total_pending} />
                    </>}
                  </SectionCard>

                  {/* Widgets (Wish List) */}
                  <SectionCard icon="🧩" title="Widgets" to="/widgets"
                    comingSoon={!wishListStats || wishListStats.total_requests === 0}>
                    {wishListStats && wishListStats.total_requests > 0 && <>
                      <StatRow label="Total requests" value={wishListStats.total_requests} highlight />
                      <StatRow label="Pending" value={wishListStats.pending_count} />
                      <StatRow label="In development" value={wishListStats.in_development_count} />
                      <StatRow label="Completed" value={wishListStats.completed_count} />
                    </>}
                  </SectionCard>

                  {/* Marketing */}
                  <SectionCard icon="📣" title="Marketing" to="/marketing"
                    comingSoon={!blogStats}>
                    {blogStats && <>
                      <StatRow label="Total posts" value={blogStats.total} highlight />
                      <StatRow label="Published" value={blogStats.published} />
                      <StatRow label="On highlight reel" value={blogStats.highlights} />
                    </>}
                  </SectionCard>

                  {/* Communications */}
                  <SectionCard icon="📧" title="Communications" to="/communications"
                    comingSoon={!commsStats}>
                    {commsStats && <>
                      <StatRow label="Emails synced" value={commsStats.totalEmails?.toLocaleString()} highlight />
                      <StatRow label="Messages synced" value={commsStats.totalMessagesSynced?.toLocaleString()} />
                      <StatRow label="Sync status" value={commsStats.syncStatus || '—'} />
                      <StatRow label="Last sync" value={commsStats.lastSyncSessionAt ? new Date(commsStats.lastSyncSessionAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'} />
                    </>}
                  </SectionCard>

                  {/* OPO */}
                  <SectionCard icon="🤖" title="OPO" to="/opo"
                    comingSoon={!opoMetrics}>
                    {opoMetrics && <>
                      <StatRow label="Legal queries (30d)" value={opoMetrics.legal_assistant?.queriesHandled ?? 0} highlight />
                      <StatRow label="OPO queries (30d)" value={opoMetrics.opo_assistant?.queriesHandled ?? 0} />
                    </>}
                  </SectionCard>

                  {/* Security */}
                  <SectionCard icon="🛡️" title="Security" to="/security"
                    comingSoon={!securityStats}
                    level={
                      !securityStats ? 'unknown'
                      : (securityStats.critical_count ?? 0) > 0 ? 'critical'
                      : (securityStats.total_findings ?? 0) > 5 ? 'warning'
                      : 'good'
                    }>
                    {securityStats && <>
                      <StatRow label="Latest scan" value={securityStats.started_at ? new Date(securityStats.started_at).toLocaleDateString() : '—'} highlight />
                      <StatRow label="Status" value={securityStats.status || '—'} />
                      <StatRow label="Critical findings" value={securityStats.critical_count ?? 0} />
                      <StatRow label="Total findings" value={securityStats.total_findings ?? 0} />
                    </>}
                  </SectionCard>

                  {/* System Monitor */}
                  <SectionCard icon="🖥️" title="System Monitor" to="/system-monitor"
                    comingSoon={!systemStats}
                    level={
                      !systemStats ? 'unknown'
                      : (systemStats.cpu?.usage ?? 0) >= 85 || (systemStats.disk?.percentage ?? 0) >= 90 ? 'critical'
                      : (systemStats.cpu?.usage ?? 0) >= 60 || (systemStats.disk?.percentage ?? 0) >= 75 ? 'warning'
                      : 'good'
                    }>
                    {systemStats && <>
                      <StatRow label="CPU usage" value={`${systemStats.cpu?.usage ?? '—'}%`} highlight />
                      <StatRow label="Memory used" value={`${fmtBytes(systemStats.memory?.used)} / ${fmtBytes(systemStats.memory?.total)}`} />
                      <StatRow label="Disk used" value={`${systemStats.disk?.percentage ?? '—'}%`} />
                      <StatRow label="Uptime" value={fmtUptime(systemStats.uptime)} />
                    </>}
                  </SectionCard>

                  {/* Backups */}
                  <SectionCard icon="☁️" title="Backups" to="/backups"
                    comingSoon={!backupStats}
                    level={
                      !backupStats ? 'unknown'
                      : backupStats.latestDailyTar ? 'good'
                      : 'warning'
                    }>
                    {backupStats && <>
                      <StatRow label="Local backup disk" value={backupStats.summary?.localBackupDirSize || '—'} highlight />
                      <StatRow label="Daily tars (local)" value={backupStats.summary?.localDailyTarCount ?? '—'} />
                      <StatRow label="Latest tar" value={backupStats.summary?.latestDailyTar?.name?.replace('nodes_backup_', '').replace('.tar.gz', '') || '—'} />
                      <StatRow label="OneDrive daily tar" value={backupStats.summary?.oneDriveDailyTar?.name ? 'Uploaded' : 'Pending'} />
                    </>}
                  </SectionCard>

                </div>

                {/* OptiPrime / OptiLaw DB footprint — summary only; full inventory: sidebar → Databases */}
                {dbInventoryLoaded && (
                  <div
                    className="db-overview-footprint"
                    style={{
                      marginTop: '1.25rem',
                      padding: '14px 16px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '10px',
                      background: 'var(--card-bg)',
                    }}
                  >
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 8px 0' }}>Databases at a glance</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', margin: '0 0 14px 0', lineHeight: 1.45 }}>
                      PostgreSQL and SQLite counts for OptiPrime and OptiLaw. For paths, sizes, and every platform, open{' '}
                      <Link to="/databases" style={{ color: '#19EFFF', fontWeight: 600 }}>Databases</Link> in the left navigation.
                    </p>
                    {!dbInventory ? (
                      <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: 0 }}>Database inventory unavailable.</p>
                    ) : (
                  ['optiprime', 'optilaw'].map((pid, idx) => {
                    const { postgres, sqlite } = countDbEnginesByPlatform(dbInventory, pid);
                    const label = pid === 'optiprime' ? 'OptiPrime' : 'OptiLaw';
                    return (
                      <div key={pid} style={{ marginBottom: idx === 0 ? '14px' : 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '6px', color: 'var(--text-primary)' }}>{label}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                          <span className="stat-pill total">PostgreSQL DBs ({postgres})</span>
                          <span className="stat-pill active">SQLite ({sqlite})</span>
                        </div>
                      </div>
                    );
                  })
                    )}
                  </div>
                )}
              </div>
              {/* ===================== END SECTION OVERVIEW ===================== */}

              {/* User Analytics */}
              <div className="analytics-section" style={{ marginTop: '2rem' }}>
                <h2>📊 User Analytics Dashboard</h2>

                <div className="analytics-row">
                  {/* Online Status */}
                  <div className="analytics-card online-status-card">
                    <div className="card-header">
                      <h3>🔴🟢 Online Status</h3>
                      <span className="card-subtitle">Live sessions (activity ≤5 min)</span>
                    </div>
                    <div className="online-stats-container">
                      {(() => {
                        const onlineUsers = loggedInNowUsers;
                        const offlineUsers = allUsers.filter(u => u.isLoggedIn !== true);
                        const pct = allUsers.length > 0 ? ((onlineUsers.length / allUsers.length) * 100).toFixed(1) : 0;
                        return (
                          <>
                            <div className="status-indicators">
                              <div className="status-item online">
                                <div className="status-dot online"></div>
                                <div className="status-content">
                                  <span className="status-number">{onlineUsers.length}</span>
                                  <span className="status-label">Logged in now</span>
                                </div>
                              </div>
                              <div className="status-item offline">
                                <div className="status-dot offline"></div>
                                <div className="status-content">
                                  <span className="status-number">{offlineUsers.length}</span>
                                  <span className="status-label">Not logged in</span>
                                </div>
                              </div>
                            </div>
                            <div className="progress-bar-container">
                              <div className="progress-bar">
                                <div className="progress-fill online" style={{ width: `${pct}%` }}></div>
                              </div>
                              <span className="progress-text">{pct}% logged in now</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Account Status */}
                  <div className="analytics-card account-status-card">
                    <div className="card-header">
                      <h3>🔒 Account Status</h3>
                      <span className="card-subtitle">Administrative control</span>
                    </div>
                    <div className="account-pills-container">
                      {(() => {
                        const enabledUsers = allUsers.filter(u => u.accountEnabled);
                        const disabledUsers = allUsers.filter(u => !u.accountEnabled);
                        const pct = allUsers.length > 0 ? ((enabledUsers.length / allUsers.length) * 100).toFixed(0) : 0;
                        return (
                          <>
                            <div className="pill-stats">
                              <div className="pill-item enabled">
                                <div className="pill-icon">🟢</div>
                                <div className="pill-content">
                                  <div className="pill-number">{enabledUsers.length}</div>
                                  <div className="pill-label">Enabled</div>
                                  <div className="pill-percentage">{pct}%</div>
                                </div>
                              </div>
                              <div className="pill-item disabled">
                                <div className="pill-icon">🔴</div>
                                <div className="pill-content">
                                  <div className="pill-number">{disabledUsers.length}</div>
                                  <div className="pill-label">Disabled</div>
                                  <div className="pill-percentage">{100 - Number(pct)}%</div>
                                </div>
                              </div>
                            </div>
                            <div className="donut-chart">
                              <svg width="120" height="120" viewBox="0 0 120 120">
                                <circle cx="60" cy="60" r="45" fill="none" stroke="#e5e7eb" strokeWidth="10"/>
                                <circle cx="60" cy="60" r="45" fill="none" stroke="#10b981" strokeWidth="10"
                                  strokeDasharray={`${pct * 2.83} 283`} strokeDashoffset="70.75" transform="rotate(-90 60 60)"/>
                                <text x="60" y="65" textAnchor="middle" className="donut-text">{pct}%</text>
                              </svg>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Language + Activity Timeline */}
                <div className="analytics-row">
                  <div className="analytics-card language-card">
                    <div className="card-header">
                      <h3>🌐 Language Preferences</h3>
                      <span className="card-subtitle">User language breakdown</span>
                    </div>
                    <div className="language-stats">
                      {(() => {
                        const languageMap = { en: { name: 'English', flag: '🇨🇦' }, fr: { name: 'French', flag: '🇫🇷' }, yue: { name: 'Cantonese', flag: '🇭🇰' }, zh: { name: 'Chinese (Simplified)', flag: '🇨🇳' }, ar: { name: 'Arabic', flag: '🌍' } };
                        const langCounts = {};
                        allUsers.forEach(u => { const l = u.language || u.language_preference || 'en'; langCounts[l] = (langCounts[l] || 0) + 1; });
                        const sorted = Object.entries(langCounts).sort(([,a],[,b]) => b - a).slice(0, 6);
                        return (
                          <div className="language-grid">
                            {sorted.map(([code, count], i) => {
                              const lang = languageMap[code] || { name: code.toUpperCase(), flag: '🌍' };
                              const pct = allUsers.length > 0 ? ((count / allUsers.length) * 100).toFixed(1) : 0;
                              return (
                                <div key={code} className="language-item">
                                  <div className="language-header">
                                    <span className="language-flag">{lang.flag}</span>
                                    <span className="language-name">{lang.name}</span>
                                  </div>
                                  <div className="language-bar">
                                    <div className="language-fill" style={{ width: `${pct}%`, backgroundColor: `hsl(${i * 60}, 70%, 60%)` }}></div>
                                  </div>
                                  <div className="language-stats-text">
                                    <span className="language-count">{count}</span>
                                    <span className="language-percentage">{pct}%</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="analytics-card activity-card">
                    <div className="card-header">
                      <h3>⚡ Activity Timeline</h3>
                      <span className="card-subtitle">Recent user activity</span>
                    </div>
                    <div className="activity-timeline">
                      {[
                        { label: 'Last Hour', hours: 1, color: '#10b981' },
                        { label: 'Last 6 Hours', hours: 6, color: '#3b82f6' },
                        { label: 'Last 24 Hours', hours: 24, color: '#8b5cf6' },
                        { label: 'Last Week', hours: 168, color: '#f59e0b' },
                        { label: 'Inactive (>1 week)', hours: Infinity, color: '#ef4444' },
                      ].map((range, index, arr) => {
                        const prevHours = index > 0 ? arr[index - 1].hours : 0;
                        const count = range.hours === Infinity
                          ? allUsers.filter(u => !u.lastLogin || (now - new Date(u.lastLogin)) > 168 * 3600 * 1000).length
                          : allUsers.filter(u => {
                              if (!u.lastLogin) return false;
                              const h = (now - new Date(u.lastLogin)) / 3600000;
                              return h >= prevHours && h < range.hours;
                            }).length;
                        const pct = allUsers.length > 0 ? ((count / allUsers.length) * 100).toFixed(0) : 0;
                        return (
                          <div key={range.label} className="timeline-item">
                            <div className="timeline-dot" style={{ backgroundColor: range.color }}></div>
                            <div className="timeline-content">
                              <div className="timeline-label">{range.label}</div>
                              <div className="timeline-stats">
                                <span className="timeline-count">{count}</span>
                                <span className="timeline-percentage">({pct}%)</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Global Buildings Map */}
              <div className="analytics-section">
                <h2>🌍 Global Buildings Map</h2>
                <div className="analytics-row">
                  <div className="analytics-card buildings-map-card" style={{ width: '100%' }}>
                    <div className="card-header">
                      <h3>📍 Building Locations</h3>
                      <span className="card-subtitle">
                        {allBuildings.filter(b => b.latitude && b.longitude).length} of {allBuildings.length} buildings have coordinates
                      </span>
                    </div>
                    {loading ? (
                      <div style={{ padding: '40px', textAlign: 'center' }}><p>Loading buildings...</p></div>
                    ) : allBuildings.filter(b => b.latitude && b.longitude).length > 0 ? (
                      <div style={{ height: '500px', width: '100%', marginTop: '15px', borderRadius: '8px', overflow: 'hidden' }}>
                        <MapContainer center={[20, 0]} zoom={2} style={{ height: '100%', width: '100%' }}>
                          <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            crossOrigin={true} />
                          {allBuildings.filter(b => b.latitude && b.longitude).map(b => (
                            <Marker key={b.id} position={[parseFloat(b.latitude), parseFloat(b.longitude)]}>
                              <Popup>
                                <div style={{ minWidth: '200px' }}>
                                  <strong>{b.name}</strong><br />
                                  {b.street_number && b.street_name && <>{b.street_number} {b.street_name}<br /></>}
                                  {b.city && <>{b.city}<br /></>}
                                  {b.country && <>{b.country}<br /></>}
                                  {b.phone_number && <><br />📞 {b.country_code} {b.phone_number}<br /></>}
                                  <br />
                                  <a href={`https://www.google.com/maps?q=${b.latitude},${b.longitude}`} target="_blank" rel="noopener noreferrer"
                                    style={{ color: '#007bff', textDecoration: 'underline', fontSize: '12px' }}>
                                    📍 View on Google Maps
                                  </a>
                                </div>
                              </Popup>
                            </Marker>
                          ))}
                        </MapContainer>
                      </div>
                    ) : (
                      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                        <p>📍 No buildings with GPS coordinates found</p>
                        <p style={{ fontSize: '14px', marginTop: '10px' }}>Add coordinates to buildings in the Buildings section to see them on the map</p>
                      </div>
                    )}
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', textAlign: 'center' }}>Map updates automatically as buildings are added or coordinates updated</p>
                  </div>
                </div>
              </div>

              {/* Feedback System Stats */}
              <div className="reports-section">
                <h2>🎯 Feedback System Overview</h2>
                <div className="reports-grid">
                  <div className="report-card">
                    <h3>📝 Total Feedback</h3>
                    <div className="report-stats">
                      <div className="stat-item"><span className="stat-label">Total Submissions:</span><span className="stat-value">{feedbackStats?.total_feedback ?? 0}</span></div>
                      <div className="stat-item"><span className="stat-label">Unique Contributors:</span><span className="stat-value">{feedbackStats?.unique_users ?? 0}</span></div>
                    </div>
                  </div>
                  <div className="report-card">
                    <h3>⚡ Workflow Status</h3>
                    <div className="report-stats">
                      <div className="stat-item"><span className="stat-label">Pending Review:</span><span className="stat-value">{feedbackStats?.total_pending ?? 0}</span></div>
                      <div className="stat-item"><span className="stat-label">In Processing:</span><span className="stat-value">{feedbackStats?.total_processed ?? 0}</span></div>
                      <div className="stat-item"><span className="stat-label">Approved:</span><span className="stat-value">{feedbackStats?.total_approved ?? 0}</span></div>
                    </div>
                  </div>
                  <div className="report-card">
                    <h3>🌍 User Distribution</h3>
                    <div className="country-list">
                      {usersByCountry.length > 0 ? usersByCountry.slice(0, 5).map((item, i) => (
                        <div key={i} className="country-item"><span>{item.country}</span><span>{item.count} user{item.count !== 1 ? 's' : ''}</span></div>
                      )) : <div className="country-item"><span style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>No data</span></div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Widget Wish List Stats */}
              <div className="reports-section">
                <h2>🚀 Widget Wish List Overview</h2>
                <div className="reports-grid">
                  <div className="report-card">
                    <h3>💡 Community Requests</h3>
                    <div className="report-stats">
                      <div className="stat-item"><span className="stat-label">Total Requests:</span><span className="stat-value">{wishListStats?.total_requests || 0}</span></div>
                      <div className="stat-item"><span className="stat-label">Unique Contributors:</span><span className="stat-value">{wishListStats?.unique_users || 0}</span></div>
                    </div>
                  </div>
                  <div className="report-card">
                    <h3>⚡ Development Pipeline</h3>
                    <div className="report-stats">
                      <div className="stat-item"><span className="stat-label">Pending Review:</span><span className="stat-value">{wishListStats?.pending_count || 0}</span></div>
                      <div className="stat-item"><span className="stat-label">In Development:</span><span className="stat-value">{wishListStats?.in_development_count || 0}</span></div>
                      <div className="stat-item"><span className="stat-label">Completed:</span><span className="stat-value">{wishListStats?.completed_count || 0}</span></div>
                    </div>
                  </div>
                  <div className="report-card">
                    <h3>📊 Popular Categories</h3>
                    <div className="country-list">
                      {(wishListStats?.categories || []).slice(0, 5).length > 0
                        ? (wishListStats.categories.slice(0, 5).map((item, i) => (
                          <div key={i} className="country-item"><span>{item.category}</span><span>{item.count} requests</span></div>
                        )))
                        : <div className="country-item"><span style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>No requests yet</span></div>
                      }
                    </div>
                  </div>
                </div>
              </div>

              {/* Blog Management Stats */}
              <div className="reports-section">
                <h2>📣 Marketing Overview</h2>
                <div className="reports-grid">
                  <div className="report-card">
                    <h3>📄 Blog Posts</h3>
                    <div className="report-stats">
                      <div className="stat-item"><span className="stat-label">Total Posts:</span><span className="stat-value">{blogStats?.total ?? 0}</span></div>
                      <div className="stat-item"><span className="stat-label">Published:</span><span className="stat-value">{blogStats?.published ?? 0}</span></div>
                      <div className="stat-item"><span className="stat-label">On Highlight Reel:</span><span className="stat-value">{blogStats?.highlights ?? 0}</span></div>
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <Link to="/marketing" className="refresh-btn" style={{ display: 'inline-block', textDecoration: 'none' }}>Open Marketing →</Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Health Section — REAL DATA */}
              <div className="system-health-section">
                <h2>🔧 System Health</h2>
                <div className="health-grid">
                  <div className="health-card">
                    <h3>🚀 Service Status</h3>
                    <div className="service-list">
                      {[
                        { name: 'Database API', check: allUsers.length >= 0 },
                        { name: 'Feedback API', check: feedbackStats !== undefined },
                        { name: 'Communications', check: commsStats !== undefined },
                        { name: 'Security (OptiGuard)', check: securityStats !== undefined },
                        { name: 'System Monitor', check: systemStats !== null },
                      ].map(svc => (
                        <div key={svc.name} className="service-item">
                          <span className="service-name">{svc.name}</span>
                          <span className={`status-badge ${svc.check ? 'online' : 'offline'}`}>
                            {svc.check ? '🟢 Online' : '🔴 Offline'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="health-card">
                    <h3>📊 Performance</h3>
                    {systemStats ? (
                      <div className="performance-stats">
                        <div className="perf-item"><span className="perf-label">CPU Usage:</span><span className="perf-value">{systemStats.cpu?.usage ?? '—'}%</span></div>
                        <div className="perf-item"><span className="perf-label">CPU Cores:</span><span className="perf-value">{systemStats.cpu?.cores ?? '—'}</span></div>
                        <div className="perf-item"><span className="perf-label">Memory Used:</span><span className="perf-value">{fmtBytes(systemStats.memory?.used)}</span></div>
                        <div className="perf-item"><span className="perf-label">Total Memory:</span><span className="perf-value">{fmtBytes(systemStats.memory?.total)}</span></div>
                        <div className="perf-item"><span className="perf-label">Disk Used:</span><span className="perf-value">{systemStats.disk?.percentage ?? '—'}%</span></div>
                        <div className="perf-item"><span className="perf-label">Server Uptime:</span><span className="perf-value">{fmtUptime(systemStats.uptime)}</span></div>
                      </div>
                    ) : (
                      <div className="performance-stats">
                        <div className="perf-item"><span className="perf-label">Status:</span><span className="perf-value">Loading…</span></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

// FeedbackPage Component
function FeedbackPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);

  // Apply theme to document root
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>🎯 Feedback</h1>
            </div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button 
                className="icon-button logout-button" 
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">
            <div className="card feedback-card opo-tactical feedback-shell">
              <div className="card-header feedback-shell-header">
                <h2 className="feedback-shell-title">
                  <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
                  INTELLIGENT FEEDBACK SYSTEM
                </h2>
                <div className="card-actions">
                  <span className="feedback-shell-tag">AI-POWERED TRIAGE</span>
                </div>
              </div>
              <div className="card-content feedback-shell-content">
                <FeedbackManager />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

// WidgetsPage Component - OptiPrime Widgets + Co-Lab Widgets
function WidgetsPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);
  const [activeTab, setActiveTab] = useState('optiprime');
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Widget detail modal state
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [detailTab, setDetailTab] = useState('details');
  const [widgetUsers, setWidgetUsers] = useState([]);
  const [widgetUsersLoading, setWidgetUsersLoading] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const loadCatalog = () => {
    setCatalogLoading(true);
    fetch('/api/community/widgets')
      .then(r => r.ok ? r.json() : [])
      .then(data => { setCatalog(Array.isArray(data) ? data : []); setCatalogLoading(false); })
      .catch(() => setCatalogLoading(false));
  };

  React.useEffect(() => { loadCatalog(); }, []);

  const openWidget = (w) => {
    setSelectedWidget(w);
    setEditFields({ name: w.name, description: w.description || '', status: w.status, url: w.url || '', iconName: w.iconName || '' });
    setDetailTab('details');
    setSaveMsg('');
    // Fetch allocated users
    setWidgetUsersLoading(true);
    fetch(`/api/community/widgets/${w.id}/users`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setWidgetUsers(Array.isArray(data) ? data : []); setWidgetUsersLoading(false); })
      .catch(() => setWidgetUsersLoading(false));
  };

  const closeWidget = () => { setSelectedWidget(null); setSaveMsg(''); };

  const handleSaveWidget = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`/api/community/widgets/${selectedWidget.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editFields),
      });
      const data = await res.json();
      if (res.ok) {
        setSaveMsg('Saved successfully.');
        // Patch the catalog entry and the selectedWidget
        setCatalog(prev => prev.map(w => w.id === selectedWidget.id ? { ...w, ...data.widget } : w));
        setSelectedWidget(prev => ({ ...prev, ...data.widget }));
      } else {
        setSaveMsg('Error: ' + (data.error || 'Failed to save'));
      }
    } catch {
      setSaveMsg('Network error while saving.');
    } finally {
      setSaving(false);
    }
  };

  const optiPrimeWidgets = catalog.filter(w => w.category === 'optiprime');

  // Tactical aggregates across the full widget catalog
  const totalWidgets = catalog.length;
  const activeWidgets = catalog.filter(w => w.status === 'active').length;
  const comingSoonWidgets = catalog.filter(w => w.status === 'coming_soon').length;
  const disabledWidgets = catalog.filter(w => !['active', 'coming_soon'].includes(w.status)).length;
  const totalAllocations = catalog.reduce((sum, w) => sum + (Number(w.userCount) || 0), 0);
  const activePct = totalWidgets ? (activeWidgets / totalWidgets) * 100 : 0;
  const comingSoonPct = totalWidgets ? (comingSoonWidgets / totalWidgets) * 100 : 0;

  const widgetsLevel = (() => {
    if (catalogLoading || totalWidgets === 0) return 'unknown';
    if (activeWidgets === 0) return 'critical';
    if (activePct < 50 || disabledWidgets > totalWidgets * 0.3) return 'warning';
    return 'good';
  })();
  const widgetsLabel = {
    good: 'WIDGET CATALOG ONLINE',
    warning: 'PARTIAL COVERAGE',
    critical: 'NO ACTIVE WIDGETS',
    unknown: 'AWAITING TELEMETRY',
  }[widgetsLevel];

  const statusLevel = (status) => {
    if (status === 'active') return 'good';
    if (status === 'coming_soon') return 'warning';
    return 'critical';
  };
  const statusBadge = (status) => {
    const level = statusLevel(status);
    const label = status === 'active' ? 'ACTIVE' : status === 'coming_soon' ? 'COMING SOON' : 'DISABLED';
    return <span className={`widgets-status-badge widgets-status-${level}`}>{label}</span>;
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1><i className="fas fa-th-large" style={{ marginRight: 10 }}></i>Widgets</h1>
            </div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button
                className="icon-button logout-button"
                onClick={() => { localStorage.removeItem('authToken'); localStorage.removeItem('userSession'); window.location.href = '/'; }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">
            {/* ─── Tactical hero + KPI strip ─────────────────────────── */}
            <div className="opo-tactical widgets-tactical">
              <div className={`opo-hero opo-hero-${widgetsLevel}`}>
                <div className="opo-hero-scan" aria-hidden="true" />
                <div className="opo-hero-left">
                  <div className="opo-hero-eyebrow">WIDGETS CATALOG</div>
                  <div className="opo-hero-title">
                    <span className={`opo-hero-pulse opo-pulse-${widgetsLevel}`} />
                    <h2>{widgetsLabel}</h2>
                  </div>
                  <div className="opo-hero-sub">
                    {totalWidgets} catalog entr{totalWidgets === 1 ? 'y' : 'ies'} · {activeWidgets} active · {comingSoonWidgets} coming soon · {totalAllocations.toLocaleString()} user allocation{totalAllocations === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="opo-hero-right">
                  <button
                    type="button"
                    className="opo-refresh-btn"
                    onClick={loadCatalog}
                    title="Re-fetch widget catalog"
                  >
                    <span className="opo-refresh-icon" /> SYNC
                  </button>
                  <div className="opo-hero-timestamp">
                    {new Date().toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="opo-kpi-row">
                <div className="opo-kpi">
                  <div className="opo-kpi-label">REGISTERED WIDGETS</div>
                  <div className="opo-kpi-value opo-mono-value">{totalWidgets.toLocaleString()}</div>
                  <div className="opo-kpi-foot">{optiPrimeWidgets.length} OptiPrime · {Math.max(0, totalWidgets - optiPrimeWidgets.length)} other</div>
                </div>
                <div className="opo-kpi">
                  <div className="opo-kpi-label">ACTIVE</div>
                  <div className="opo-kpi-value opo-mono-value opo-level-good">{activeWidgets}<span className="opo-kpi-sep">/</span>{totalWidgets}</div>
                  <div className="opo-bar-track opo-bar-mini">
                    <div className="opo-bar-fill opo-fill-good" style={{ width: `${activePct}%` }} />
                  </div>
                  <div className="opo-kpi-foot">{activePct.toFixed(0)}% online</div>
                </div>
                <div className="opo-kpi">
                  <div className="opo-kpi-label">COMING SOON</div>
                  <div className="opo-kpi-value opo-mono-value opo-level-warning">{comingSoonWidgets}</div>
                  <div className="opo-bar-track opo-bar-mini">
                    <div className="opo-bar-fill opo-fill-warning" style={{ width: `${comingSoonPct}%` }} />
                  </div>
                  <div className="opo-kpi-foot">{disabledWidgets} disabled</div>
                </div>
                <div className="opo-kpi">
                  <div className="opo-kpi-label">USER ALLOCATIONS</div>
                  <div className="opo-kpi-value opo-mono-value">{totalAllocations.toLocaleString()}</div>
                  <div className="opo-kpi-foot">{(totalWidgets > 0 ? totalAllocations / totalWidgets : 0).toFixed(1)} avg / widget</div>
                </div>
              </div>
            </div>

            {/* Tactical Tab Strip */}
            <div className="widgets-tab-strip" role="tablist">
              {[
                { key: 'optiprime', label: 'OPTIPRIME WIDGETS', icon: 'fa-th-large', count: optiPrimeWidgets.length },
                { key: 'colab', label: 'CO-LAB WIDGETS', icon: 'fa-rocket', count: null },
              ].map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`widgets-tab-btn${activeTab === tab.key ? ' is-active' : ''}`}
                >
                  <i className={`fas ${tab.icon}`} />
                  <span className="widgets-tab-label">{tab.label}</span>
                  {tab.count !== null && (
                    <span className="widgets-tab-count opo-mono-value">{tab.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* OptiPrime Widgets Tab */}
            {activeTab === 'optiprime' && (
              <div className="card opo-tactical widgets-shell">
                <div className="card-header widgets-shell-header">
                  <h2 className="widgets-shell-title">
                    <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
                    OPTIPRIME WIDGETS
                    <span className="widgets-shell-count opo-mono-value">{optiPrimeWidgets.length}</span>
                  </h2>
                  <div className="card-actions">
                    <span className="widgets-shell-tag">CLICK TO INSPECT / EDIT</span>
                  </div>
                </div>
                <div className="card-content widgets-shell-content">
                  {catalogLoading ? (
                    <div className="widgets-empty">
                      <i className="fas fa-spinner fa-spin" />
                      <p>LOADING WIDGETS…</p>
                    </div>
                  ) : optiPrimeWidgets.length === 0 ? (
                    <div className="widgets-empty">
                      <i className="fas fa-puzzle-piece" />
                      <p>No OptiPrime widgets found.</p>
                    </div>
                  ) : (
                    <div className="widgets-grid">
                      {optiPrimeWidgets.map(w => {
                        const lvl = statusLevel(w.status);
                        return (
                          <div
                            key={w.id}
                            onClick={() => openWidget(w)}
                            className={`widget-card widget-card-${lvl}`}
                          >
                            <div className="widget-card-scan" aria-hidden="true" />
                            <div className="widget-card-head">
                              <div className="widget-card-icon-wrap">
                                <span className={`opo-pip opo-pip-${lvl}`}><span className="opo-pip-core" /></span>
                                <div className={`widget-card-icon widget-card-icon-${lvl}`}>
                                  <i className={`fas ${w.iconName || 'fa-puzzle-piece'}`} />
                                </div>
                                <div className="widget-card-titleblock">
                                  <div className="widget-card-name">{w.name}</div>
                                  <div className="widget-card-id">ID: {w.id}</div>
                                </div>
                              </div>
                              {statusBadge(w.status)}
                            </div>
                            <p className="widget-card-desc">{w.description || '—'}</p>
                            <div className="widget-card-foot">
                              <span className="widget-card-users">
                                <i className="fas fa-users" />
                                <span className="opo-mono-value">{w.userCount || 0}</span>
                                <span className="widget-card-users-label">user{w.userCount !== 1 ? 's' : ''} allocated</span>
                              </span>
                              <span className="widget-card-edit">
                                <i className="fas fa-pencil-alt" /> EDIT
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Co-Lab Widgets Tab */}
            {activeTab === 'colab' && (
              <div className="card feedback-card opo-tactical widgets-shell colab-shell">
                <div className="card-header widgets-shell-header">
                  <h2 className="widgets-shell-title">
                    <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
                    CO-LAB WIDGETS
                  </h2>
                  <div className="card-actions">
                    <span className="widgets-shell-tag">COMMUNITY-DRIVEN · AI TRIAGED</span>
                  </div>
                </div>
                <div className="card-content widgets-shell-content">
                  <WishListManager />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Widget Detail / Edit Modal */}
      {selectedWidget && (
        <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={e => { if (e.target === e.currentTarget) closeWidget(); }}>
          <div className="modal-content" style={{ maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Modal Header */}
            <div className="modal-header" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className={`fas ${selectedWidget.iconName || 'fa-puzzle-piece'}`} style={{ fontSize: 20, color: 'var(--accent, #2563eb)' }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>{selectedWidget.name}</h2>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>ID: {selectedWidget.id} &nbsp;·&nbsp; {statusBadge(selectedWidget.status)}</div>
              </div>
              <button className="close-button" onClick={closeWidget}><i className="fas fa-times"></i></button>
            </div>

            {/* Modal Tab Bar */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-color)', padding: '0 24px' }}>
              {[
                { key: 'details', label: 'Details', icon: 'fa-info-circle' },
                { key: 'users', label: `Allocated Users (${widgetUsers.length})`, icon: 'fa-users' },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setDetailTab(t.key)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '12px 18px',
                    fontWeight: 600, fontSize: 13,
                    color: detailTab === t.key ? 'var(--accent, #2563eb)' : 'var(--text-secondary)',
                    borderBottom: detailTab === t.key ? '2px solid var(--accent, #2563eb)' : '2px solid transparent',
                    marginBottom: -1,
                  }}
                >
                  <i className={`fas ${t.icon}`} style={{ marginRight: 6 }}></i>{t.label}
                </button>
              ))}
            </div>

            <div className="modal-body">
              {/* Details Tab */}
              {detailTab === 'details' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="form-group">
                      <label>Display Name</label>
                      <input type="text" value={editFields.name || ''} onChange={e => setEditFields(p => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select value={editFields.status || 'active'} onChange={e => setEditFields(p => ({ ...p, status: e.target.value }))}>
                        <option value="active">Active</option>
                        <option value="coming_soon">Coming Soon</option>
                        <option value="disabled">Disabled</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Icon Class <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 400 }}>(FontAwesome, e.g. fa-gavel)</span></label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="text" value={editFields.iconName || ''} onChange={e => setEditFields(p => ({ ...p, iconName: e.target.value }))} style={{ flex: 1 }} />
                        {editFields.iconName && <i className={`fas ${editFields.iconName}`} style={{ fontSize: 20, color: 'var(--accent)' }}></i>}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Launch URL</label>
                      <input type="text" value={editFields.url || ''} onChange={e => setEditFields(p => ({ ...p, url: e.target.value }))} placeholder="https://…" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea value={editFields.description || ''} onChange={e => setEditFields(p => ({ ...p, description: e.target.value }))} rows={3} style={{ resize: 'vertical', width: '100%', padding: 10, borderRadius: 6, background: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.5 }} />
                  </div>
                  {/* Read-only info */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    {[
                      { label: 'Category', value: selectedWidget.category },
                      { label: 'Sort Order', value: selectedWidget.sortOrder },
                      { label: 'Users Allocated', value: selectedWidget.userCount },
                    ].map(item => (
                      <div key={item.label} style={{ background: 'var(--input-bg, #0f172a)', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                  {saveMsg && (
                    <div style={{ fontSize: 13, color: saveMsg.startsWith('Error') ? '#ef4444' : '#16a34a', fontWeight: 600 }}>
                      <i className={`fas ${saveMsg.startsWith('Error') ? 'fa-exclamation-triangle' : 'fa-check-circle'}`} style={{ marginRight: 6 }}></i>{saveMsg}
                    </div>
                  )}
                </div>
              )}

              {/* Allocated Users Tab */}
              {detailTab === 'users' && (
                <div>
                  {widgetUsersLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <i className="fas fa-spinner fa-spin" style={{ fontSize: 20, color: 'var(--accent)' }}></i>
                      <p style={{ marginTop: 10, color: 'var(--text-secondary)', fontSize: 13 }}>Loading users…</p>
                    </div>
                  ) : widgetUsers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                      <i className="fas fa-users" style={{ fontSize: 28, marginBottom: 10, display: 'block', opacity: 0.4 }}></i>
                      <p style={{ margin: 0, fontSize: 14 }}>No users have been allocated this widget yet.</p>
                      <p style={{ margin: '8px 0 0', fontSize: 12 }}>Go to BETA Users → Edit User → Widget Permissions to allocate.</p>
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                          {['User', 'Email', 'Granted By', 'Granted At'].map(h => (
                            <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-secondary)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {widgetUsers.map(u => (
                          <tr key={u.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--text-primary)' }}>
                              <span style={{ display: 'block' }}>{u.firstName} {u.lastName}</span>
                              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>@{u.username}</span>
                            </td>
                            <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{u.email}</td>
                            <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{u.grantedBy || '—'}</td>
                            <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>
                              {u.grantedAt ? new Date(u.grantedAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--border-color)' }}>
              <button className="btn btn-secondary" onClick={closeWidget}>Close</button>
              {detailTab === 'details' && (
                <button className="btn btn-primary" onClick={handleSaveWidget} disabled={saving}>
                  {saving ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Saving…</> : <><i className="fas fa-save" style={{ marginRight: 8 }}></i>Save Changes</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </ThemeContext.Provider>
  );
}

// Keep old route name as redirect for any bookmarks
function WishListPage() {
  React.useEffect(() => { window.location.replace('/widgets'); }, []);
  return null;
}

// MarketingPage - Marketing hub (blog, social, PR, content studio, analytics)
function MarketingAdminPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>📣 Marketing</h1>
            </div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button
                className="icon-button logout-button"
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">
            <MarketingPage />
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

// Auditor — first-class accounting / compliance workspace (was a modal on /opo)
function AuditorAdminPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>Auditor</h1>
            </div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button
                className="icon-button logout-button"
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">
            <AuditorPage />
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

function BlogManagementRedirect() {
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate('/marketing?category=blog-management', { replace: true });
  }, [navigate]);
  return null;
}

function CommunicationsPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>Communications</h1>
            </div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button
                className="icon-button logout-button"
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">
            <CommunicationsDashboard sidebarCollapsed={isCollapsed} />
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

function SystemMonitorPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);

  // Apply theme to document root
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>🖥️ System Monitor</h1>
            </div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button 
                className="icon-button logout-button" 
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  localStorage.removeItem('sessionToken');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">
            <SystemMonitor />
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

function OPOPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);

  // Apply theme to document root
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>🧠 OptiPrime Operator</h1>
            </div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button 
                className="icon-button logout-button" 
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">
            <OPODashboard />
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

// ── Cornelius admin pages — same chrome as OPOPage / SecurityPage ──
function AdminCorneliusTasksPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);
  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title"><h1>🔬 Cornelius Tasks</h1></div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button
                className="icon-button logout-button"
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              ><i className="fas fa-sign-out-alt"></i></button>
            </div>
          </div>
          <div className="content-wrapper">
            <AdminCorneliusTasks />
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

function AdminCorneliusMemoriesPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);
  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title"><h1>🧠 Cornelius Memory</h1></div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button
                className="icon-button logout-button"
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              ><i className="fas fa-sign-out-alt"></i></button>
            </div>
          </div>
          <div className="content-wrapper">
            <AdminCorneliusMemories />
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

function SecurityPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>🛡️ OptiGuard Security</h1>
            </div>
            <div className="top-nav-actions">
              <button
                className="icon-button settings-button"
                onClick={() => {
                  const settingsEvent = new CustomEvent('openAdminSettings');
                  window.dispatchEvent(settingsEvent);
                }}
                title="Admin Settings (profile, password, MFA)"
              >
                <i className="fas fa-cog"></i>
              </button>
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button
                className="icon-button logout-button"
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper full-height">
            <SecurityDashboard />
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

function BackupsPage() {
  const { isCollapsed } = useContext(SidebarContext);
  const [isDark, setIsDark] = useState(true);
  const toggleTheme = () => setIsDark(!isDark);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className="app-container">
        <Sidebar />
        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className={`top-nav ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="nav-title">
              <h1>☁️ Backups</h1>
            </div>
            <div className="top-nav-actions">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              <button
                className="icon-button logout-button"
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userSession');
                  window.location.href = '/';
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
          <div className="content-wrapper">
            <BackupsDashboard />
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

function App() {
  const [showAdminSettings, setShowAdminSettings] = useState(false);

  // Listen for admin settings events
  useEffect(() => {
    const handleOpenAdminSettings = () => setShowAdminSettings(true);
    window.addEventListener('openAdminSettings', handleOpenAdminSettings);
    return () => window.removeEventListener('openAdminSettings', handleOpenAdminSettings);
  }, []);

  return (
    <SidebarProvider>
    <Router>
      <DemoModeBanner />
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/reset-password" element={<PasswordReset />} />
        <Route path="/dashboard" element={<DatabaseDashboard />} />
        <Route path="/databases" element={<DatabasesPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/retail-users" element={<RetailUsersPage />} />
        <Route path="/packages" element={<PackageManagementPage />} />
        <Route path="/buildings" element={<BuildingsPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/widgets" element={<WidgetsPage />} />
        <Route path="/wish-list" element={<WishListPage />} />
        <Route path="/marketing" element={<MarketingAdminPage />} />
        <Route path="/blog-management" element={<BlogManagementRedirect />} />
        <Route path="/communications" element={<CommunicationsPage />} />
        <Route path="/opo" element={<OPOPage />} />
        <Route path="/opo/auditor/:tab?/:agent?" element={<AuditorAdminPage />} />
        <Route path="/opo/document/:documentId" element={<SpreadsheetViewerPage />} />
        <Route path="/opo/t2/review/:token" element={<T2ReviewPage />} />
        <Route path="/admin/cornelius-tasks" element={<AdminCorneliusTasksPage />} />
        <Route path="/admin/cornelius-memories" element={<AdminCorneliusMemoriesPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/backups" element={<BackupsPage />} />
        <Route path="/system-monitor" element={<SystemMonitorPage />} />
      </Routes>
      
      {/* Admin Settings Modal */}
      <AdminSettingsModal 
        isOpen={showAdminSettings} 
        onClose={() => setShowAdminSettings(false)} 
      />
    </Router>
    </SidebarProvider>
  );
}

export { SidebarContext };
export default App;
